import { chatGPTBackendModel, chatGPTQualities } from '@/lib/chatgptImageModels';
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CommandIcon, ArrowRightIcon, CaretDownIcon, CaretLeftIcon, CaretRightIcon, UserIcon, MapPinIcon, PaletteIcon, CropIcon, CameraIcon, EyeIcon, StackIcon, ArrowsOutIcon, DeviceMobileIcon, MonitorIcon, FilmStripIcon, FilmSlateIcon, MagnifyingGlassPlusIcon, ArrowUpIcon, ArrowDownIcon, XIcon, CpuIcon, PlusIcon, Image as ImageIcon, GearSixIcon, Link as LinkIcon, UploadIcon, TrashIcon, SunIcon, ClockIcon, CheckSquareIcon, SquareIcon, LockIcon, ShuffleIcon, ApertureIcon, LightningIcon, HouseIcon, BuildingIcon, TreeIcon, CloudIcon, MoonIcon, GhostIcon, RocketIcon, FolderOpenIcon, FloppyDiskIcon, LayoutIcon } from '@phosphor-icons/react';
import { GeneratorSelections } from '../types';
import { useAuth } from '../context/AuthContext';
import { useProject } from '../context/ProjectContext';
import { useToast } from '../context/ToastContext';
import { enhancePrompt, getAspectRatio, PROMPT_MAPPINGS } from '../lib/promptEnhancer';
import { Session } from '../lib/sessions';
import { CharacterSelector, CharacterRef } from './CharacterSelector';
import { AVAILABLE_MODELS, ModelConfig, getVisibleModels, DEFAULT_IMAGE_MODEL, DEFAULT_IMAGE_MODEL_NAME } from '@/lib/model-config';
import { getCreditCost, formatCreditCost, canAccessModel } from '@/lib/credits';
import { CreditCostBadge } from './CreditCostBadge';
import { InsufficientCreditsModal } from './InsufficientCreditsModal';
import { supabase } from '../lib/supabase';
import { getStyleById } from '../lib/characterStyles';
import { CharacterArchetype } from '../types';
import { uploadCharacterImageToR2 } from '../lib/character2dService';

// Extended character reference with passport data for Nano Banana
export interface Character2DRef {
  id: string;
  name: string;
  headImageUrl: string; // head layer image for avatar display
  avatarYPosition: number; // crop position for avatar (0-100, default 30)
  referenceSheetUrl: string; // full reference sheet for generation
  usagePrompt: string; // the "magic prompt" from passport
  textDescription: string; // canonical text description
  archetype: string; // film, game_3d, manga_anime, comic_cartoon
  stylePreset: string; // style ID from characterStyles.ts
}


// --- Icons ---
const GoogleLogo = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" xmlns="http://www.w3.org/2000/svg">
    <g transform="matrix(1, 0, 0, 1, 0, 0)">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </g>
  </svg>
);

const FluxLogo = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3L2 21h20L12 3z" fill="white" stroke="none" />
    <path d="M12 8v8" stroke="black" strokeWidth="1.5" />
  </svg>
);

interface GeneratorProps {
  onGenerate: (prompt: string, modelId?: string, options?: { aspectRatio?: string, inputImage?: string, referenceImages?: string[], selections?: Record<string, string | undefined>, characterReference?: CharacterRef, character2DRef?: Character2DRef, resolution?: string }, originalPrompt?: string) => void;

  variant?: 'floating' | 'docked';
  isGenerating?: boolean;
  forcedValues?: {
    prompt?: string;
    selections?: Partial<GeneratorSelections>;
    model?: string; // Model ID from the generation
    clearInput?: boolean; // Signal to clear all input state
    characterReference?: Character2DRef | null; // Character used in generation (null = clear, undefined = don't change)
  };
  onInputChange?: () => void; // Called when user modifies prompt or selections
  activeTool?: string;
  onStateChange?: (state: { hasPrompt: boolean; hasFilters: boolean; hasImages: boolean }) => void;
  onToolChange?: (tool: string) => void;
  // Session props
  sessions?: Session[];
  currentSession?: Session | null;
  onSessionSelect?: (session: Session | null) => void;
  onSessionCreate?: (name: string) => void;
  onSessionSave?: () => void;
  onSessionDelete?: (sessionId: string) => void;
  viewMode?: 'lookbook' | 'gallery' | 'storyboard';
  onViewModeChange?: (mode: 'lookbook' | 'gallery' | 'storyboard') => void;
}

export const GeneratorInput: React.FC<GeneratorProps> = ({ onGenerate, variant = 'floating', isGenerating = false, forcedValues, onInputChange, activeTool = 'prompt', onStateChange, onToolChange, sessions = [], currentSession, onSessionSelect, onSessionCreate, onSessionSave, onSessionDelete, viewMode, onViewModeChange }) => {
  const { user, login, creditBalance } = useAuth();
  const { showToast } = useToast();

  // Insufficient credits modal state
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [creditsRequired, setCreditsRequired] = useState(0);

  const examples = [
    "A marketing poster for coffee with steam rising...",
    "A cute 3D Pixar-style character holding a sign...",
    "A futuristic city with flying cars...",
    "A hyper-realistic perfume bottle on a podium...",
    "A funny cat video wearing sunglasses..."
  ];

  const [promptValue, setPromptValue] = useState("");
  const promptInputRef = useRef<HTMLElement>(null);

  // Sync promptValue to contentEditable text (one-way sync for external updates)
  useEffect(() => {
    if (promptInputRef.current && promptInputRef.current.innerText !== promptValue) {
      promptInputRef.current.innerText = promptValue;
      // Move cursor to end after programmatic update to fix typing issues
      if (promptValue && document.activeElement === promptInputRef.current) {
        const range = document.createRange();
        const sel = window.getSelection();
        range.selectNodeContents(promptInputRef.current);
        range.collapse(false); // Collapse to end
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
    }
  }, [promptValue]);

  const [isFocused, setIsFocused] = useState(false);
  const [activePlaceholder, setActivePlaceholder] = useState(examples[0]);
  const [typedPlaceholder, setTypedPlaceholder] = useState('');

  // Typewriter effect for placeholder
  useEffect(() => {
    if (isFocused || promptValue) {
      setTypedPlaceholder('');
      return;
    }

    let charIndex = 0;
    setTypedPlaceholder('');

    const typeInterval = setInterval(() => {
      if (charIndex < activePlaceholder.length) {
        setTypedPlaceholder(activePlaceholder.slice(0, charIndex + 1));
        charIndex++;
      } else {
        clearInterval(typeInterval);
      }
    }, 40);

    return () => clearInterval(typeInterval);
  }, [activePlaceholder, isFocused, promptValue]);

  // Cycle placeholders
  useEffect(() => {
    if (!promptValue && !isFocused) {
      const interval = setInterval(() => {
        setActivePlaceholder(prev => {
          const idx = examples.indexOf(prev);
          return examples[(idx + 1) % examples.length];
        });
      }, 4000); // Slightly longer to allow typing animation to complete
      return () => clearInterval(interval);
    }
  }, [promptValue, isFocused]);

  // UI State
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [activeModel, setActiveModel] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('ezart_last_model_v2');
      if (stored && getVisibleModels(viewMode === 'lookbook' ? user?.id : undefined).some(model => model.name === stored)) return stored;
    }
    return DEFAULT_IMAGE_MODEL_NAME;
  });
  const isChatGPTModelSelected = AVAILABLE_MODELS.some(model => model.name === activeModel && model.provider === 'chatgpt');
  const activeChatGPTQualities = isChatGPTModelSelected ? chatGPTQualities(chatGPTBackendModel(AVAILABLE_MODELS.find(model => model.name === activeModel)!.id)) : [];
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [showConfigDropdown, setShowConfigDropdown] = useState(false);

  // Image Upload State
  const [images, setImages] = useState<string[]>([]);
  const [showImageDropdown, setShowImageDropdown] = useState(false);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const imageDropdownRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const configDropdownRef = useRef<HTMLDivElement>(null);
  const modelDropdownRef = useRef<HTMLDivElement>(null);
  const modelButtonRef = useRef<HTMLButtonElement>(null);
  const modelDropdownMenuRef = useRef<HTMLDivElement>(null); // Ref for the portaled dropdown menu
  const [modelDropdownCoords, setModelDropdownCoords] = useState<{ top?: number, bottom?: number, left: number }>({ left: 0 });

  // Session name modal state
  const [showSessionNameModal, setShowSessionNameModal] = useState(false);
  const [sessionNameInput, setSessionNameInput] = useState('');

  // Session delete confirmation state
  const [sessionToDelete, setSessionToDelete] = useState<Session | null>(null);

  // Max images per model: Nano Banana supports 14, Z-Image 4, others 3
  const supportsCharacterReferences = activeModel.includes('Nano') || isChatGPTModelSelected;
  const maxImages = isChatGPTModelSelected ? 16 : activeModel.includes('Nano') ? 14 : activeModel.includes('Z-Image') ? 4 : 3;

  // Persist model selection to localStorage
  useEffect(() => {
    localStorage.setItem('ezart_last_model_v2', activeModel);
  }, [activeModel]);

  // Close all dropdowns when viewMode changes (switching tabs)
  useEffect(() => {
    setActiveDropdown(null);
    setShowModelDropdown(false);
    setShowConfigDropdown(false);
    setShowImageDropdown(false);
    setShowCharacterDropdown(false);
  }, [viewMode]);

  const [selectedCharacter, setSelectedCharacter] = useState<CharacterRef | null>(null);

  // Project-based 2D characters with passports (for Nano Banana character generation)
  const { selectedProjectId } = useProject();
  const [projectCharacters, setProjectCharacters] = useState<Character2DRef[]>([]);
  const [selectedCharacters2D, setSelectedCharacters2D] = useState<Character2DRef[]>([]);
  const [showCharacterDropdown, setShowCharacterDropdown] = useState(false);
  const [isLoadingCharacters, setIsLoadingCharacters] = useState(false);
  const characterDropdownRef = useRef<HTMLDivElement>(null);
  const characterBarRef = useRef<HTMLDivElement>(null);
  const [styleConsistency, setStyleConsistency] = useState(true); // Style consistency on by default
  const [styleConflictNotification, setStyleConflictNotification] = useState<string | null>(null);

  // Fetch finalized characters with passports for the current project when Nano Banana is selected
  useEffect(() => {
    const fetchProjectCharacters = async () => {
      if (!user || !supportsCharacterReferences) {
        setProjectCharacters([]);
        return;
      }

      setIsLoadingCharacters(true);
      try {
        // Fetch finalized characters that have passports
        // Filter by project if one is selected, otherwise show all user's characters
        let query = supabase
          .from('characters_2d')
          .select(`
            id,
            name,
            project_id,
            core_layers,
            archetype,
            style_preset,
            avatar_y_position,
            character_passports (
              id,
              reference_sheet,
              usage_prompt,
              text_description
            )
          `)
          .eq('user_id', user.id)
          .eq('is_finalized', true);

        if (selectedProjectId) {
          query = query.eq('project_id', selectedProjectId);
        }

        const { data, error } = await query.order('updated_at', { ascending: false }).limit(50);

        if (error) {
          console.error('Error fetching project characters:', error);
          setProjectCharacters([]);
          return;
        }

        // Transform to Character2DRef format
        const characters: Character2DRef[] = (data || [])
          .filter((c: any) => c.character_passports && c.character_passports.length > 0)
          .map((c: any) => {
            const passport = c.character_passports[0];
            return {
              id: c.id,
              name: c.name || 'Unnamed',
              headImageUrl: c.core_layers?.head?.web_url || c.core_layers?.head?.rendered_url || passport.reference_sheet?.front_url || '',
              avatarYPosition: c.avatar_y_position ?? 30, // Use saved crop position from database
              referenceSheetUrl: passport.reference_sheet?.front_url || passport.reference_sheet?.turnaround_url || '',
              usagePrompt: passport.usage_prompt || '',
              textDescription: passport.text_description || '',
              archetype: c.archetype || 'film',
              stylePreset: c.style_preset || 'photorealistic'
            };
          });

        setProjectCharacters(characters);
      } catch (err) {
        console.error('Failed to fetch project characters:', err);
        setProjectCharacters([]);
      } finally {
        setIsLoadingCharacters(false);
      }
    };

    fetchProjectCharacters();
  }, [user, activeModel, selectedProjectId]);

  // Character bar is toggled only by the character button - no click-outside closing

  const [selections, setSelections] = useState<GeneratorSelections>({
    subject: 'None',
    location: 'None',
    style: 'None',
    ratio: 'Square (1:1)',
    resolution: 'High Quality',
    shot: 'None',
    angle: 'None',
    lighting: 'None',
    tod: 'None'
  });

  // Sync resolution with active model on initial mount and model change
  // Also persist/restore resolution per model from localStorage
  useEffect(() => {
    const storageKey = `ezart_resolution_${activeModel.replace(/\s+/g, '_')}`;
    const storedResolution = localStorage.getItem(storageKey) || (isChatGPTModelSelected ? localStorage.getItem('ezart_resolution_ChatGPT') : null);
    if (isChatGPTModelSelected) {
      const qualityLabels: Record<string, string> = { Fast: 'Low', 'High Quality': 'Medium', 'Best Quality': 'High', Low: 'Low', Medium: 'Medium', High: 'High', 'Extra High': 'Extra High', Max: 'Max' };
      setSelections(prev => ({ ...prev, resolution: (['Extra High','Max'].includes(qualityLabels[storedResolution || '']) && !activeChatGPTQualities.includes('xhigh')) ? 'Medium' : qualityLabels[storedResolution || ''] || 'Medium' }));
      return;
    }

    if (storedResolution) {
      // Use stored resolution for this model
      setSelections(prev => ({ ...prev, resolution: storedResolution }));
    } else {
      // Set default resolution based on model
      // Nano Banana (Flash) only supports 1K, Nano Banana Pro supports 1K/2K/4K
      if (activeModel === 'Nano Banana Pro') {
        setSelections(prev => ({ ...prev, resolution: '2K' }));
      } else if (activeModel === 'Nano Banana') {
        setSelections(prev => ({ ...prev, resolution: '1080p' })); // Flash only supports 1K
      } else {
        setSelections(prev => ({ ...prev, resolution: 'High Quality' }));
      }
    }
  }, [activeModel]);

  // Persist resolution selection to localStorage per model
  useEffect(() => {
    const storageKey = `ezart_resolution_${activeModel.replace(/\s+/g, '_')}`;
    localStorage.setItem(storageKey, selections.resolution);
  }, [selections.resolution, activeModel]);

  const [visibleOptions, setVisibleOptions] = useState<Record<string, boolean>>({
    subject: true,
    location: true,
    style: true,
    shot: true,
    angle: true,
    lighting: true,
    tod: true
  });

  // Weight emphasis cycle: none → 1.1 → 1.25 → 1.3 → 1.5 → 0.9 → 0.7 → none
  const WEIGHT_CYCLE = [undefined, 1.1, 1.25, 1.3, 1.5, 0.9, 0.7];
  const [filterWeights, setFilterWeights] = useState<Record<string, number | undefined>>({});

  const cycleFilterWeight = (key: string) => {
    setFilterWeights(prev => {
      const currentWeight = prev[key];
      const currentIdx = WEIGHT_CYCLE.indexOf(currentWeight);
      const nextIdx = (currentIdx + 1) % WEIGHT_CYCLE.length;
      return { ...prev, [key]: WEIGHT_CYCLE[nextIdx] };
    });
  };

  const getWeightColor = (weight: number | undefined) => {
    if (!weight) return 'text-zinc-400';
    if (weight >= 1.3) return 'text-orange-400';
    if (weight >= 1.1) return 'text-yellow-400';
    if (weight < 1) return 'text-blue-400';
    return 'text-zinc-400';
  };

  const applyWeightToTag = (tag: string, weight: number | undefined): string => {
    if (!weight) return tag;
    // Split by comma, wrap each part with weight
    return tag.split(', ').map(t => `(${t.trim()}:${weight})`).join(', ');
  };

  // Only show visible models in the dropdown (hides internal variants)
  const models = getVisibleModels(viewMode === 'lookbook' ? user?.id : undefined).map(m => ({
    name: m.name,
    modelId: m.id,
    pro: m.pricing.pro,
    supportsImage: m.capabilities.includes('img2img'),
    icon: m.icon
  }));

  useEffect(() => {
    if (!getVisibleModels(viewMode === 'lookbook' ? user?.id : undefined).some(model => model.name === activeModel)) {
      setActiveModel(DEFAULT_IMAGE_MODEL_NAME);
    }
  }, [viewMode, user?.id, activeModel]);

  // Map UI resolution labels to model config resolution keys for credit cost lookup
  const getResolutionKey = (uiResolution: string, modelId: string): string | undefined => {
    if (modelId.includes('nano-banana-pro') || modelId.includes('gemini-3-pro')) {
      const map: Record<string, string> = { '1080p': '1K', '2K': '2K', '4K': '4K' };
      return map[uiResolution];
    }
    if (modelId.includes('z-image-turbo')) {
      const map: Record<string, string> = { 'Standard': '0.5MP', 'High Quality': '1MP', 'Best Quality': '2MP' };
      return map[uiResolution];
    }
    return undefined;
  };

  // Get the current credit cost based on active model + resolution
  const currentModelForCost = models.find(m => m.name === activeModel);
  const currentCreditCost = currentModelForCost
    ? getCreditCost(currentModelForCost.modelId, getResolutionKey(selections.resolution, currentModelForCost.modelId))
    : 1;

  // Model-specific filter mappings
  const MODEL_FILTERS: Record<string, string[]> = {
    'google/nano-banana': [],
    'google/nano-banana-pro': [],
    'prunaai/z-image-turbo': [],
    'cagliostrolab/animagine-xl-3.1': [
      'animagine_char', 'animagine_physique', 'animagine_hair', 'animagine_eyes',
      'animagine_skin', 'animagine_attire', 'animagine_creature', 'animagine_emotion',
      'animagine_activity', 'animagine_location', 'animagine_weather', 'animagine_lighting',
      'animagine_time', 'animagine_style', 'animagine_shot', 'animagine_angle',
      'animagine_focus', 'animagine_artist', 'rating'
    ]
  };
  const currentModelConfig = models.find(m => m.name === activeModel);
  const activeModelId = currentModelConfig ? currentModelConfig.modelId : '';
  const supportsImage = currentModelConfig?.supportsImage ?? false;
  const supportedFilters = MODEL_FILTERS[activeModelId] || [];
  // Track last applied forced values to prevent repeated applications
  const lastAppliedForcedValuesRef = React.useRef<string | null>(null);
  const lastClearRef = React.useRef<boolean>(false);
  const filterScrollRef = React.useRef<HTMLDivElement>(null);

  const scrollFilters = (direction: 'left' | 'right') => {
    if (filterScrollRef.current) {
      const scrollAmount = 150;
      filterScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    if (!forcedValues) return;

    // Handle clearInput signal
    if (forcedValues.clearInput && !lastClearRef.current) {
      lastClearRef.current = true;
      setPromptValue('');
      setSelections(prev => ({ ratio: prev.ratio, resolution: prev.resolution }));
      setFilterWeights({});
      setSelectedCharacters2D([]); // Clear character selection on clearInput
      lastAppliedForcedValuesRef.current = null;
      return;
    } else if (!forcedValues.clearInput) {
      lastClearRef.current = false;
    }

    // Create a signature to detect if forcedValues actually changed
    // Deterministic stringify by sorting keys to prevent loops from prop object reference changes
    const deterministicStringify = (obj: any) => {
      if (typeof obj !== 'object' || obj === null) return JSON.stringify(obj);
      return JSON.stringify(Object.keys(obj).sort().reduce((acc: any, key) => {
        acc[key] = obj[key];
        return acc;
      }, {}));
    };

    const signature = JSON.stringify({
      prompt: forcedValues.prompt,
      selections: deterministicStringify(forcedValues.selections),
      model: forcedValues.model,
      characterId: forcedValues.characterReference?.id
    });

    // Skip if we already applied these exact values
    if (lastAppliedForcedValuesRef.current === signature) return;
    lastAppliedForcedValuesRef.current = signature;

    console.log('📋 forcedValues received:', JSON.stringify(forcedValues, null, 2));

    // Determine if we need to switch models
    let targetModelName = activeModel;
    if (forcedValues.model) {
      console.log('🔍 forcedValues.model:', forcedValues.model);
      const matchingModel = models.find(m => m.modelId === forcedValues.model);
      console.log('🔍 matchingModel:', matchingModel);
      if (matchingModel) {
        targetModelName = matchingModel.name;
        if (matchingModel.name !== activeModel) {
          console.log('🔄 Switching model from', activeModel, 'to', matchingModel.name);
          setActiveModel(matchingModel.name);
        }
      }
    }

    // Check if we have meaningful selections (excluding ratio and resolution which are always set)
    const hasSelections = forcedValues.selections && Object.keys(forcedValues.selections).some(k => {
      if (k === 'ratio' || k === 'resolution') return false; // Don't count these as "meaningful" selections
      const v = forcedValues.selections![k];
      return v && (Array.isArray(v) ? v.length > 0 : v !== 'None' && v !== 'Default');
    });
    console.log('🎯 hasSelections:', hasSelections, 'keys:', forcedValues.selections ? Object.keys(forcedValues.selections) : 'none');

    // Always restore ratio and resolution from selections if present
    if (forcedValues.selections?.ratio || forcedValues.selections?.resolution) {
      setSelections(prev => ({
        ...prev,
        ...(forcedValues.selections?.ratio && { ratio: forcedValues.selections.ratio }),
        ...(forcedValues.selections?.resolution && { resolution: forcedValues.selections.resolution })
      }));
    }

    if (hasSelections) {
      // We have meaningful selections (beyond ratio/resolution) - merge them all
      console.log('✅ Restoring selections:', forcedValues.selections);
      setSelections(prev => ({ ...prev, ...forcedValues.selections }));
      // Also show the prompt (contains the tag representation for Animagine, etc.)
      if (forcedValues.prompt) {
        setPromptValue(forcedValues.prompt);
      }
    } else if (forcedValues.prompt) {
      // No selections stored - set prompt directly for ALL models
      setPromptValue(forcedValues.prompt);

      // Reset standard selections to defaults (animagine-specific keys will just be ignored by other models)
      setSelections(prev => ({
        ...prev,
        subject: 'None',
        location: 'None',
        style: 'None',
        shot: 'None',
        angle: 'None',
        lighting: 'None',
        tod: 'None'
      }));
    }

    // Restore character reference if present in forcedValues
    // Clear if explicitly null (image selected that doesn't have a character)
    if (forcedValues.characterReference) {
      console.log('🎭 Restoring character reference:', forcedValues.characterReference.name);
      setSelectedCharacters2D([forcedValues.characterReference]);
    } else if (forcedValues.characterReference === null) {
      // Explicitly clear character selection when image doesn't have a character
      console.log('🎭 Clearing character reference (image has no character)');
      setSelectedCharacters2D([]);
    }
    // Note: undefined means don't change (for backwards compatibility)
  }, [forcedValues, activeModel]);

  // Switch away from builder tool if model doesn't support it
  React.useEffect(() => {
    if (activeTool === 'builder' && !activeModel.includes('Animagine')) {
      onToolChange?.('prompt');
    }
  }, [activeModel, activeTool, onToolChange]);

  const options: Record<string, any[]> = {
    // Standard Filters (Flux)
    subject: [
      { label: 'Cyber Samurai', icon: UserIcon },
      { label: 'Product Shot', icon: BoxIcon },
      { label: '3D Character', icon: UserIcon },
      { label: 'Abstract Art', icon: PaletteIcon },
    ],
    location: [
      { label: 'Studio Background', icon: MapPinIcon },
      { label: 'Neon City', icon: MapPinIcon },
      { label: 'Nature', icon: MapPinIcon },
      { label: 'Space', icon: MapPinIcon },
    ],
    // ... (others kept same)
    style: [
      { label: 'Cinematic', icon: PaletteIcon },
      { label: '3D Render', icon: PaletteIcon },
      { label: 'Photorealistic', icon: PaletteIcon },
      { label: 'Anime', icon: PaletteIcon },
      { label: 'Minimalist', icon: PaletteIcon },
    ],
    ratio: activeModel.includes('Animagine') ? [
      { label: 'Square (1:1)', icon: CropIcon },
      { label: 'Standard (9:7)', icon: BoxIcon },
      { label: 'Vertical (7:9)', icon: BoxIcon },
      { label: 'Photo (3:2)', icon: MonitorIcon },
      { label: 'Photo Portrait (2:3)', icon: DeviceMobileIcon },
      { label: 'Wide (7:4)', icon: FilmSlateIcon },
      { label: 'Tall (4:7)', icon: DeviceMobileIcon },
      { label: 'Panorama (12:5)', icon: ArrowsOutIcon },
      { label: 'Vertical Pan (5:12)', icon: ArrowsOutIcon },
    ] : [
      { label: 'Square (1:1)', icon: CropIcon },
      { label: 'Standard (9:7)', icon: BoxIcon },
      { label: 'Vertical (7:9)', icon: BoxIcon },
      { label: 'Classic (4:3)', icon: BoxIcon },
      { label: 'Classic Portrait (3:4)', icon: BoxIcon },
      { label: 'Photo (3:2)', icon: MonitorIcon },
      { label: 'Photo Portrait (2:3)', icon: DeviceMobileIcon },
      { label: 'Widescreen (16:9)', icon: MonitorIcon },
      { label: 'Mobile (9:16)', icon: DeviceMobileIcon },
      { label: 'Cinematic (21:9)', icon: FilmSlateIcon },
      { label: 'Tall (9:21)', icon: DeviceMobileIcon },
    ],
    // Resolution options - varies by model
    // Nano Banana (Flash) only supports 1K, Nano Banana Pro supports 1K/2K/4K
    resolution: isChatGPTModelSelected ? [
      { label: 'Low', icon: MonitorIcon },
      { label: 'Medium', icon: ArrowsOutIcon },
      { label: 'High', icon: ArrowsOutIcon },
      ...(activeChatGPTQualities.includes('xhigh') ? [{label:'Extra High',icon:ArrowsOutIcon},{label:'Max',icon:ArrowsOutIcon}] : []),
    ] : activeModel === 'Nano Banana Pro' ? [
      { label: '1080p', icon: MonitorIcon },
      { label: '2K', icon: ArrowsOutIcon },
      { label: '4K', icon: ArrowsOutIcon },
    ] : activeModel === 'Nano Banana' ? [
      { label: '1080p', icon: MonitorIcon }, // Flash only supports 1K
    ] : [
      { label: 'Fast', icon: MonitorIcon },
      { label: 'High Quality', icon: ArrowsOutIcon },
      { label: 'Best Quality', icon: ArrowsOutIcon },
    ],
    // Animagine Specific Filters
    animagine_char: [
      { label: 'Female', icon: UserIcon },
      { label: 'Male', icon: UserIcon },
      { label: 'Duo (F/F)', icon: UserIcon },
      { label: 'Duo (M/M)', icon: UserIcon },
      { label: 'Duo (M/F)', icon: UserIcon },
      { label: 'Group (F)', icon: UserIcon },
      { label: 'Group (M)', icon: UserIcon },
      { label: 'Group (M/F)', icon: UserIcon },
      { label: 'Non-Human', icon: GhostIcon },
    ],
    animagine_attire: [
      // Headwear
      { label: 'Hat', icon: PaletteIcon }, { label: 'Cap', icon: PaletteIcon }, { label: 'Baseball Cap', icon: PaletteIcon },
      { label: 'Beanie', icon: PaletteIcon }, { label: 'Beret', icon: PaletteIcon }, { label: 'Fedora', icon: PaletteIcon },
      { label: 'Top Hat', icon: PaletteIcon }, { label: 'Cowboy Hat', icon: PaletteIcon }, { label: 'Witch Hat', icon: PaletteIcon },
      { label: 'Helmet', icon: PaletteIcon }, { label: 'Visored Helmet', icon: PaletteIcon },
      { label: 'Hair Ribbon', icon: PaletteIcon }, { label: 'Hair Bow', icon: PaletteIcon }, { label: 'Hairband', icon: PaletteIcon },
      { label: 'Headband', icon: PaletteIcon }, { label: 'Hair Flower', icon: PaletteIcon }, { label: 'Hair Ornament', icon: PaletteIcon },
      { label: 'Kanzashi', icon: PaletteIcon },
      // Tops
      { label: 'T-Shirt', icon: PaletteIcon }, { label: 'Shirt', icon: PaletteIcon }, { label: 'Blouse', icon: PaletteIcon },
      { label: 'Tank Top', icon: PaletteIcon }, { label: 'Camisole', icon: PaletteIcon }, { label: 'Tube Top', icon: PaletteIcon },
      { label: 'Crop Top', icon: PaletteIcon }, { label: 'Hoodie', icon: PaletteIcon }, { label: 'Sweater', icon: PaletteIcon },
      { label: 'Cardigan', icon: PaletteIcon }, { label: 'Blazer', icon: PaletteIcon }, { label: 'Jacket', icon: PaletteIcon },
      { label: 'Coat', icon: PaletteIcon }, { label: 'Trench Coat', icon: PaletteIcon }, { label: 'Cape', icon: PaletteIcon },
      { label: 'Cloak', icon: PaletteIcon }, { label: 'Robe', icon: PaletteIcon },
      { label: 'Kimono', icon: PaletteIcon }, { label: 'Yukata', icon: PaletteIcon }, { label: 'Haori', icon: PaletteIcon },
      // Bottoms
      { label: 'Skirt', icon: PaletteIcon }, { label: 'Miniskirt', icon: PaletteIcon }, { label: 'Micro Skirt', icon: PaletteIcon },
      { label: 'Pleated Skirt', icon: PaletteIcon }, { label: 'Pencil Skirt', icon: PaletteIcon }, { label: 'Long Skirt', icon: PaletteIcon },
      { label: 'Bubble Skirt', icon: PaletteIcon }, { label: 'Pants', icon: PaletteIcon }, { label: 'Jeans', icon: PaletteIcon },
      { label: 'Denim', icon: PaletteIcon }, { label: 'Shorts', icon: PaletteIcon }, { label: 'Denim Shorts', icon: PaletteIcon },
      { label: 'Leggings', icon: PaletteIcon }, { label: 'Yoga Pants', icon: PaletteIcon }, { label: 'Sweatpants', icon: PaletteIcon },
      { label: 'Bloomers', icon: PaletteIcon }, { label: 'Dolphin Shorts', icon: PaletteIcon }, { label: 'Hakama', icon: PaletteIcon },
      // Legwear
      { label: 'Socks', icon: PaletteIcon }, { label: 'Ankle Socks', icon: PaletteIcon }, { label: 'Kneehighs', icon: PaletteIcon },
      { label: 'Over-Kneehighs', icon: PaletteIcon }, { label: 'Thighhighs', icon: PaletteIcon },
      { label: 'Pantyhose', icon: PaletteIcon }, { label: 'Tights', icon: PaletteIcon }, { label: 'Fishnets', icon: PaletteIcon },
      { label: 'Garter Straps', icon: PaletteIcon }, { label: 'Zettai Ryouiki', icon: PaletteIcon },
      // Footwear
      { label: 'Shoes', icon: PaletteIcon }, { label: 'Sneakers', icon: PaletteIcon }, { label: 'Boots', icon: PaletteIcon },
      { label: 'Knee Boots', icon: PaletteIcon }, { label: 'Thigh Boots', icon: PaletteIcon }, { label: 'Combat Boots', icon: PaletteIcon },
      { label: 'Loafers', icon: PaletteIcon }, { label: 'Mary Janes', icon: PaletteIcon },
      { label: 'High Heels', icon: PaletteIcon }, { label: 'Pumps', icon: PaletteIcon }, { label: 'Stilettos', icon: PaletteIcon },
      { label: 'Sandals', icon: PaletteIcon }, { label: 'Flip-Flops', icon: PaletteIcon }, { label: 'Barefoot', icon: PaletteIcon },
      // Accessories
      { label: 'Glasses', icon: PaletteIcon }, { label: 'Sunglasses', icon: PaletteIcon }, { label: 'Goggles', icon: PaletteIcon },
      { label: 'Monocle', icon: PaletteIcon }, { label: 'Eyepatch', icon: PaletteIcon },
      { label: 'Jewelry', icon: PaletteIcon }, { label: 'Necklace', icon: PaletteIcon }, { label: 'Earrings', icon: PaletteIcon },
      { label: 'Bracelet', icon: PaletteIcon }, { label: 'Choker', icon: PaletteIcon }, { label: 'Ring', icon: PaletteIcon },
      { label: 'Gloves', icon: PaletteIcon }, { label: 'Fingerless Gloves', icon: PaletteIcon }, { label: 'Elbow Gloves', icon: PaletteIcon },
      { label: 'Scarf', icon: PaletteIcon }, { label: 'Neckerchief', icon: PaletteIcon }, { label: 'Necktie', icon: PaletteIcon }, { label: 'Bowtie', icon: PaletteIcon },
    ],
    animagine_physique: [
      // Body Type
      { label: 'Slim', icon: UserIcon }, { label: 'Skinny', icon: UserIcon }, { label: 'Slender', icon: UserIcon },
      { label: 'Muscular', icon: UserIcon }, { label: 'Toned', icon: UserIcon }, { label: 'Abs', icon: UserIcon },
      { label: 'Defined Abs', icon: UserIcon }, { label: 'Muscular Female', icon: UserIcon },
      { label: 'Plump', icon: UserIcon }, { label: 'Chubby', icon: UserIcon }, { label: 'Curvy', icon: UserIcon },
      { label: 'Voluptuous', icon: UserIcon }, { label: 'Wide Hips', icon: UserIcon }, { label: 'Thick Thighs', icon: UserIcon },
      { label: 'Tall', icon: UserIcon }, { label: 'Petite', icon: UserIcon }, { label: 'Short', icon: UserIcon },
      { label: 'Flat Chest', icon: UserIcon }, { label: 'Small Breasts', icon: UserIcon }, { label: 'Medium Breasts', icon: UserIcon },
      { label: 'Large Breasts', icon: UserIcon }, { label: 'Huge Breasts', icon: UserIcon }, { label: 'Gigantic Breasts', icon: UserIcon },
    ],
    animagine_creature: [
      // Animal Features
      { label: 'Cat Ears', icon: GhostIcon }, { label: 'Fox Ears', icon: GhostIcon }, { label: 'Bunny Ears', icon: GhostIcon },
      { label: 'Elf Ears', icon: GhostIcon },
      { label: 'Angel Wings', icon: GhostIcon }, { label: 'Demon Wings', icon: GhostIcon }, { label: 'Fairy Wings', icon: GhostIcon },
      { label: 'Demon Horns', icon: GhostIcon }, { label: 'Oni Horns', icon: GhostIcon },
      { label: 'Demon Tail', icon: GhostIcon }, { label: 'Fluffy Tail', icon: GhostIcon },
      { label: 'Scales', icon: GhostIcon }, { label: 'Centaur', icon: GhostIcon }, { label: 'Mermaid', icon: GhostIcon },
    ],
    animagine_hair: [
      // Length
      { label: 'Pixie Cut', icon: UserIcon }, { label: 'Short Hair', icon: UserIcon }, { label: 'Medium Hair', icon: UserIcon },
      { label: 'Long Hair', icon: UserIcon }, { label: 'Very Long Hair', icon: UserIcon }, { label: 'Absurdly Long Hair', icon: UserIcon },
      // Colors
      { label: 'Purple Hair', icon: PaletteIcon }, { label: 'Blue Hair', icon: PaletteIcon }, { label: 'Green Hair', icon: PaletteIcon },
      { label: 'Yellow Hair', icon: PaletteIcon }, { label: 'Orange Hair', icon: PaletteIcon }, { label: 'Brown Hair', icon: PaletteIcon },
      { label: 'Red Hair', icon: PaletteIcon }, { label: 'Pink Hair', icon: PaletteIcon },
      { label: 'Black Hair', icon: PaletteIcon }, { label: 'White Hair', icon: PaletteIcon }, { label: 'Grey Hair', icon: PaletteIcon },
      { label: 'Blonde Hair', icon: PaletteIcon },
      // Color Effects
      { label: 'Gradient Hair', icon: PaletteIcon }, { label: 'Two-Tone Hair', icon: PaletteIcon }, { label: 'Inner Color', icon: PaletteIcon },
      { label: 'Streaked Hair', icon: PaletteIcon }, { label: 'Rainbow Hair', icon: PaletteIcon },
      // Bangs
      { label: 'Blunt Bangs', icon: UserIcon }, { label: 'Swept Bangs', icon: UserIcon }, { label: 'Hair Between Eyes', icon: UserIcon },
      { label: 'Hair Over Eye', icon: UserIcon }, { label: 'Blindfold Hair', icon: UserIcon },
      // Styles
      { label: 'Ponytail', icon: UserIcon }, { label: 'Side Ponytail', icon: UserIcon }, { label: 'High Ponytail', icon: UserIcon },
      { label: 'Low Ponytail', icon: UserIcon }, { label: 'Twintails', icon: UserIcon }, { label: 'Low Twintails', icon: UserIcon },
      { label: 'Short Twintails', icon: UserIcon }, { label: 'Braid', icon: UserIcon }, { label: 'Single Braid', icon: UserIcon },
      { label: 'Twin Braids', icon: UserIcon }, { label: 'Crown Braid', icon: UserIcon }, { label: 'French Braid', icon: UserIcon },
      { label: 'Hair Bun', icon: UserIcon }, { label: 'Double Bun', icon: UserIcon }, { label: 'Braided Bun', icon: UserIcon },
      // Texture
      { label: 'Straight Hair', icon: UserIcon }, { label: 'Wavy Hair', icon: UserIcon }, { label: 'Curly Hair', icon: UserIcon },
      { label: 'Ringlets', icon: UserIcon }, { label: 'Spiked Hair', icon: UserIcon }, { label: 'Messy Hair', icon: UserIcon },
      { label: 'Disheveled Hair', icon: UserIcon }, { label: 'Ahoge', icon: UserIcon }, { label: 'Floating Hair', icon: UserIcon },
    ],
    animagine_eyes: [
      // Colors
      { label: 'Purple Eyes', icon: EyeIcon }, { label: 'Blue Eyes', icon: EyeIcon }, { label: 'Green Eyes', icon: EyeIcon },
      { label: 'Yellow Eyes', icon: EyeIcon }, { label: 'Orange Eyes', icon: EyeIcon }, { label: 'Red Eyes', icon: EyeIcon },
      { label: 'Brown Eyes', icon: EyeIcon },
      // Shape
      { label: 'Tsurime', icon: EyeIcon }, { label: 'Tareme', icon: EyeIcon }, { label: 'Slit Pupils', icon: EyeIcon },
      { label: 'Flower Pupils', icon: EyeIcon }, { label: 'Heart Pupils', icon: EyeIcon }, { label: 'Star Pupils', icon: EyeIcon },
      { label: 'Symbol Pupils', icon: EyeIcon },
      // States
      { label: 'Closed Eyes', icon: EyeIcon }, { label: 'Winking', icon: EyeIcon }, { label: 'Half-Closed Eyes', icon: EyeIcon },
      { label: 'Empty Eyes', icon: EyeIcon }, { label: 'Glowing Eyes', icon: EyeIcon },
    ],
    animagine_emotion: [
      // Happy
      { label: 'Smile', icon: UserIcon }, { label: 'Open Mouth', icon: UserIcon }, { label: 'Laughing', icon: UserIcon },
      { label: 'Grin', icon: UserIcon }, { label: 'V-Brows', icon: UserIcon }, { label: 'Blush', icon: UserIcon },
      { label: 'Blush Lines', icon: UserIcon }, { label: 'Heart Eyes', icon: UserIcon }, { label: 'Sparkles', icon: UserIcon },
      { label: 'Wide-Eyed', icon: UserIcon },
      // Sad
      { label: 'Crying', icon: UserIcon }, { label: 'Tears', icon: UserIcon }, { label: 'Frown', icon: UserIcon },
      { label: 'Sad', icon: UserIcon }, { label: 'Gloomy', icon: UserIcon }, { label: 'Depressed', icon: UserIcon },
      // Angry
      { label: 'Angry', icon: UserIcon }, { label: 'Annoyed', icon: UserIcon }, { label: 'Furrowed Brow', icon: UserIcon },
      { label: 'Clenched Teeth', icon: UserIcon }, { label: 'Shouting', icon: UserIcon },
      // Scared
      { label: 'Scared', icon: UserIcon }, { label: 'Terror', icon: UserIcon }, { label: 'Screaming', icon: UserIcon },
      { label: 'Pale Face', icon: UserIcon },
      // Other
      { label: 'Seductive Smile', icon: UserIcon }, { label: 'Licking Lips', icon: UserIcon }, { label: 'Heavy Breathing', icon: UserIcon },
      { label: 'Tongue Out', icon: UserIcon }, { label: 'Wink', icon: UserIcon },
      { label: 'Embarrassed', icon: UserIcon }, { label: 'Hiding Face', icon: UserIcon }, { label: 'Looking Away', icon: UserIcon },
      { label: 'Expressionless', icon: UserIcon }, { label: 'Pout', icon: UserIcon }, { label: 'Sleeping', icon: UserIcon },
      { label: 'Yawning', icon: UserIcon },
    ],
    animagine_style: [
      // Digital
      { label: 'Digital Painting', icon: PaletteIcon }, { label: 'Digital Media', icon: PaletteIcon }, { label: 'CGI', icon: PaletteIcon },
      { label: '3D Render', icon: PaletteIcon }, { label: 'Blender', icon: PaletteIcon },
      // Traditional
      { label: 'Traditional', icon: PaletteIcon }, { label: 'Watercolor', icon: PaletteIcon }, { label: 'Oil Painting', icon: PaletteIcon },
      { label: 'Ink', icon: PaletteIcon }, { label: 'Graphite', icon: PaletteIcon }, { label: 'Pencil Sketch', icon: PaletteIcon },
      { label: 'Charcoal', icon: PaletteIcon },
      // Vector/Line
      { label: 'Vector', icon: PaletteIcon }, { label: 'Lineart', icon: PaletteIcon }, { label: 'Flat Color', icon: PaletteIcon },
      { label: 'Cel Shading', icon: PaletteIcon },
      // Pixel
      { label: 'Pixel Art', icon: PaletteIcon }, { label: '16-bit', icon: PaletteIcon }, { label: 'Dithering', icon: PaletteIcon },
      // Era/Aesthetic
      { label: '80s Style', icon: PaletteIcon }, { label: '90s Style', icon: PaletteIcon }, { label: 'Retro', icon: PaletteIcon },
      { label: 'Gothic', icon: PaletteIcon }, { label: 'Kawaii', icon: PaletteIcon }, { label: 'Cyberpunk', icon: PaletteIcon },
      { label: 'Steampunk', icon: PaletteIcon }, { label: 'Art Nouveau', icon: PaletteIcon },
      // Color
      { label: 'Monochrome', icon: PaletteIcon }, { label: 'Greyscale', icon: PaletteIcon }, { label: 'Sepia', icon: PaletteIcon },
    ],
    animagine_activity: [
      { label: 'Standing', icon: UserIcon },
      { label: 'Sitting', icon: UserIcon },
      { label: 'Kneeling', icon: UserIcon },
      { label: 'Lying Down', icon: UserIcon },
      { label: 'Walking', icon: UserIcon },
      { label: 'Running', icon: UserIcon },
      { label: 'Fighting', icon: UserIcon },
      { label: 'Dancing', icon: UserIcon },
      { label: 'Eating', icon: UserIcon },
      { label: 'Sleeping', icon: UserIcon },
    ],
    animagine_location: [
      { label: 'Simple Background', icon: BoxIcon },
      { label: 'White Background', icon: BoxIcon },
      { label: 'Transparent Background', icon: BoxIcon },
      { label: 'Gradient Background', icon: BoxIcon },
      { label: 'Detailed Background', icon: BoxIcon },
      { label: 'Bedroom', icon: HouseIcon },
      { label: 'Living Room', icon: HouseIcon },
      { label: 'Kitchen', icon: HouseIcon },
      { label: 'Bathroom', icon: HouseIcon },
      { label: 'Foyer', icon: HouseIcon },
      { label: 'Tatami Room', icon: HouseIcon },
      { label: 'Classroom', icon: BuildingIcon },
      { label: 'School Corridor', icon: BuildingIcon },
      { label: 'Gymnasium', icon: BuildingIcon },
      { label: 'Library', icon: BuildingIcon },
      { label: 'Science Room', icon: BuildingIcon },
      { label: 'Shop', icon: BuildingIcon },
      { label: 'Convenience Store', icon: BuildingIcon },
      { label: 'Restaurant', icon: BuildingIcon },
      { label: 'Cafe', icon: BuildingIcon },
      { label: 'Bar', icon: BuildingIcon },
      { label: 'Dungeon', icon: GhostIcon },
      { label: 'Castle', icon: BuildingIcon },
      { label: 'Throne Room', icon: BuildingIcon },
      { label: 'Spaceship', icon: RocketIcon },
      { label: 'Laboratory', icon: BuildingIcon },
      { label: 'Forest', icon: TreeIcon },
      { label: 'Woods', icon: TreeIcon },
      { label: 'Tree', icon: TreeIcon },
      { label: 'Flower Field', icon: TreeIcon },
      { label: 'Meadow', icon: TreeIcon },
      { label: 'Garden', icon: TreeIcon },
      { label: 'Jungle', icon: TreeIcon },
      { label: 'Beach', icon: MapPinIcon },
      { label: 'Ocean', icon: MapPinIcon },
      { label: 'Lake', icon: MapPinIcon },
      { label: 'River', icon: MapPinIcon },
      { label: 'Underwater', icon: MapPinIcon },
      { label: 'City', icon: BuildingIcon },
      { label: 'Cityscape', icon: BuildingIcon },
      { label: 'Street', icon: MapPinIcon },
      { label: 'Alley', icon: MapPinIcon },
      { label: 'Rooftop', icon: BuildingIcon },
      { label: 'Park', icon: TreeIcon },
      { label: 'Train Station', icon: BuildingIcon },
      { label: 'Ruins', icon: MapPinIcon },
      { label: 'Desert', icon: MapPinIcon },
      { label: 'Wasteland', icon: MapPinIcon },
      { label: 'Snowy Landscape', icon: MapPinIcon },
      { label: 'Blue Sky', icon: CloudIcon },
      { label: 'Cloudy Sky', icon: CloudIcon },
      { label: 'Starry Sky', icon: MoonIcon },
      { label: 'Night Sky', icon: MoonIcon },
      { label: 'Moon', icon: MoonIcon },
      { label: 'Full Moon', icon: MoonIcon },
      { label: 'Fireworks', icon: MoonIcon },
    ],
    animagine_shot: [
      { label: 'Close-Up', icon: CameraIcon },
      { label: 'Portrait', icon: CameraIcon },
      { label: 'Upper Body', icon: CameraIcon },
      { label: 'Cowboy Shot', icon: CameraIcon },
      { label: 'Full Body', icon: CameraIcon },
      { label: 'Wide Shot', icon: CameraIcon },
    ],
    animagine_angle: [
      { label: 'Eye Contact', icon: EyeIcon },
      { label: 'Looking Back', icon: UserIcon },
      { label: 'Profile', icon: UserIcon },
      { label: 'From Above', icon: ArrowUpIcon },
      { label: 'From Below', icon: ArrowDownIcon },
      { label: 'Dutch Angle', icon: CropIcon },
      { label: 'Fisheye', icon: EyeIcon },
    ],
    animagine_focus: [
      { label: 'Depth of Field', icon: ApertureIcon },
      { label: 'Bokeh', icon: ApertureIcon },
      { label: 'Foreground Focus', icon: ApertureIcon },
      { label: 'Motion Blur', icon: LightningIcon },
    ],
    animagine_lighting: [
      { label: 'Cinematic', icon: SunIcon },
      { label: 'Volumetric', icon: SunIcon },
      { label: 'Backlighting', icon: SunIcon },
      { label: 'Rim Lighting', icon: SunIcon },
      { label: 'Soft Lighting', icon: SunIcon },
      { label: 'Bioluminescence', icon: SunIcon },
      { label: 'Neon Lights', icon: SunIcon },
    ],
    animagine_weather: [
      { label: 'Sunny', icon: CloudIcon },
      { label: 'Rain', icon: CloudIcon },
      { label: 'Snowing', icon: CloudIcon },
      { label: 'Storm', icon: CloudIcon },
      { label: 'Fog', icon: CloudIcon },
      { label: 'Windy', icon: CloudIcon },
    ],
    animagine_time: [
      { label: 'Day', icon: ClockIcon },
      { label: 'Noon', icon: ClockIcon },
      { label: 'Sunset', icon: ClockIcon },
      { label: 'Twilight', icon: ClockIcon },
      { label: 'Dusk', icon: ClockIcon },
      { label: 'Night', icon: ClockIcon },
      { label: 'Midnight', icon: ClockIcon },
    ],
    animagine_skin: [
      { label: 'Pale', icon: UserIcon },
      { label: 'White', icon: UserIcon },
      { label: 'Tan', icon: UserIcon },
      { label: 'Tan Lines', icon: UserIcon },
      { label: 'Dark', icon: UserIcon },
      { label: 'Very Dark', icon: UserIcon },
      { label: 'Blue', icon: UserIcon },
      { label: 'Green', icon: UserIcon },
      { label: 'Red', icon: UserIcon },
      { label: 'Grey', icon: UserIcon },
      { label: 'Pale Blue', icon: UserIcon },
      { label: 'Purple', icon: UserIcon },
    ],

    shot: [
      { label: 'Macro Detail', icon: MagnifyingGlassPlusIcon },
      { label: 'Product Zoom', icon: MagnifyingGlassPlusIcon },
      { label: 'Waist Up', icon: UserIcon },
      { label: 'Wide View', icon: ArrowsOutIcon },
    ],
    angle: [
      { label: 'Eye Level', icon: EyeIcon },
      { label: 'From Above', icon: ArrowUpIcon },
      { label: 'From Below', icon: ArrowDownIcon },
      { label: 'Top Down', icon: StackIcon },
    ],
    lighting: [
      { label: 'Studio', icon: SunIcon },
      { label: 'Natural', icon: SunIcon },
      { label: 'Cinematic', icon: SunIcon },
      { label: 'Neon', icon: SunIcon },
      { label: 'Softbox', icon: SunIcon },
    ],
    tod: [
      { label: 'Golden Hour', icon: ClockIcon },
      { label: 'High Noon', icon: ClockIcon },
      { label: 'Blue Hour', icon: ClockIcon },
      { label: 'Midnight', icon: ClockIcon },
    ],

    // Animagine Specific Filters
    quality: [
      { label: 'Masterpiece', icon: CheckSquareIcon }, // > 95%
      { label: 'Best Quality', icon: CheckSquareIcon }, // > 85%
      { label: 'Great Quality', icon: CheckSquareIcon }, // > 75%
      { label: 'Good Quality', icon: CheckSquareIcon }, // > 50%
      { label: 'Normal Quality', icon: CheckSquareIcon }, // > 25%
    ],
    aesthetic: [
      { label: 'Very Aesthetic', icon: PaletteIcon }, // > 0.71
      { label: 'Aesthetic', icon: PaletteIcon }, // > 0.45
      { label: 'Displeasing', icon: TrashIcon }, // View first completeness, mapped to 'displeasing' (rarely used but good control)
    ],
    era: [
      { label: 'Newest (2021+)', icon: ClockIcon },
      { label: 'Recent (2018-20)', icon: ClockIcon },
      { label: 'Mid (2015-17)', icon: ClockIcon },
      { label: 'Early (2011-14)', icon: ClockIcon },
      { label: 'Oldest (2005-10)', icon: ClockIcon },
    ],
    rating: [
      { label: 'Safe', icon: LockIcon }, // General
      { label: 'Sensitive', icon: LockIcon },
      { label: 'NSFW', icon: m => <span className="text-[10px] font-bold text-red-500">18+</span> },
      { label: 'Explicit', icon: m => <span className="text-[10px] font-bold text-red-600">X</span> },
    ],
    // Dynamic List from Mappings - sorted so artists with numbers come last
    animagine_artist: Object.keys(PROMPT_MAPPINGS.animagine_artist || {})
      .sort((a, b) => {
        const aHasNumber = /\d/.test(a);
        const bHasNumber = /\d/.test(b);
        if (aHasNumber && !bHasNumber) return 1; // a goes after b
        if (!aHasNumber && bHasNumber) return -1; // a goes before b
        return a.localeCompare(b); // alphabetical within group
      })
      .map(label => ({
        label,
        icon: PaletteIcon
      })),
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (imageDropdownRef.current && !imageDropdownRef.current.contains(event.target as Node)) {
        setShowImageDropdown(false);
        setShowLinkInput(false);
      }
      if (configDropdownRef.current && !configDropdownRef.current.contains(event.target as Node)) {
        setShowConfigDropdown(false);
      }
      if (modelDropdownRef.current && !modelDropdownRef.current.contains(event.target as Node) &&
          modelDropdownMenuRef.current && !modelDropdownMenuRef.current.contains(event.target as Node)) {
        setShowModelDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculate model dropdown position for portal rendering
  useEffect(() => {
    if (showModelDropdown && modelButtonRef.current) {
      const updatePosition = () => {
        const rect = modelButtonRef.current?.getBoundingClientRect();
        if (!rect) return;
        const spaceBelow = window.innerHeight - rect.bottom;
        const openUp = spaceBelow < 300;

        if (openUp) {
          setModelDropdownCoords({ bottom: window.innerHeight - rect.top + 4, left: rect.left });
        } else {
          setModelDropdownCoords({ top: rect.bottom + 4, left: rect.left });
        }
      };
      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);
      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition, true);
      };
    }
  }, [showModelDropdown]);

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const toggleDropdown = (key: string) => {
    setActiveDropdown(activeDropdown === key ? null : key);
  };

  const selectOption = (key: string, value: string | string[]) => {
    setSelections(prev => ({ ...prev, [key]: value }));
    // Only close dropdown for single-select (not arrays)
    if (!Array.isArray(value)) {
      setActiveDropdown(null);
    }
    onInputChange?.(); // Notify parent that user modified input
  };

  const toggleOptionVisibility = (key: string) => {
    setVisibleOptions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getModelIcon = (modelName: string) => {
    // Check if model has a custom icon in config
    const modelWithIcon = models.find(m => m.name === modelName);
    if (modelWithIcon?.icon) {
      return <img src={modelWithIcon.icon} className="w-3.5 h-3.5 object-cover" alt={modelName} />;
    }
    // Fallback to built-in icons
    if (modelName.includes('Pollinations')) return <CpuIcon className="w-3 h-3" />;
    if (modelName.startsWith('Flux')) return <FluxLogo />;
    if (modelName.includes('Nano')) return <GoogleLogo />;
    if (modelName.includes('Recraft')) return <PaletteIcon className="w-3 h-3 text-purple-400" />;
    if (modelName.includes('Ideogram')) return <span className="text-[10px] font-bold">Ig</span>;
    if (modelName.includes('SDXL')) return <LightningIcon className="w-3 h-3 text-yellow-400" />;
    if (modelName.includes('Seedream')) return <img src="https://github.com/bytedance.png" className="w-3.5 h-3.5 rounded-full" alt="Seedream" />;
    return <CpuIcon className="w-3 h-3" />;
  };

  const handleGenerateClick = async () => {
    // Prevent starting new generation while one is in progress
    if (isGenerating) return;

    if (!user) {
      login();
      return;
    }
    // Find the model ID for the selected model name
    const selectedModel = models.find(m => m.name === activeModel);
    const modelId = selectedModel?.modelId || DEFAULT_IMAGE_MODEL;

    // Filter selections based on which filter toggles are active or model-specific
    // Dynamic Filter Construction based on Model Support
    const activeSelections: Record<string, string | undefined> = {
      ratio: selections.ratio, // Always included (not displayed in prompt)
      resolution: selections.resolution, // Always included - required like ratio
    };

    // Only include filters supported by the current model (use modelId, not name)
    const currentModelFilters = MODEL_FILTERS[modelId] || [];
    console.log('🔧 MODEL_FILTERS lookup:', { modelId, currentModelFilters, allSelections: selections });
    currentModelFilters.forEach(key => {
      const val = (selections as any)[key];
      console.log(`  - Checking ${key}:`, val);
      if (val) {
        activeSelections[key] = val;
      }
    });
    console.log('📦 Final activeSelections:', activeSelections);

    // Enhance prompt with only active filters
    console.log("DEBUG: validate modelId", modelId);
    const enhancedPrompt = enhancePrompt(promptValue, activeSelections, modelId as any, filterWeights);
    const aspectRatio = getAspectRatio(selections);

    console.log('✨ Enhanced Prompt:', enhancedPrompt);
    console.log('>>>> FINAL ANIMAGINE PROMPT:', enhancedPrompt);
    console.log('📐 Aspect Ratio:', aspectRatio);

    // Pass enhanced prompt for generation, but also pass original prompt for display
    const inputImage = images[0];

    // If 2D characters with passports are selected, incorporate their usage_prompts
    let finalPrompt = enhancedPrompt;
    // Only set character reference if the model actually supports characters (Nano models)
    let characterRef: CharacterRef | undefined = supportsCharacterReferences ? (selectedCharacter || undefined) : undefined;

    if (selectedCharacters2D.length > 0 && supportsCharacterReferences) {
      // Get style keywords if style consistency is enabled
      // Returns both archetype base style AND specific substyle keywords
      const getStyleKeywords = (char: Character2DRef): string => {
        if (!styleConsistency) return '';

        // Get the base archetype style (e.g., "anime, manga, masterpiece")
        const archetypePrompts: Record<string, string> = {
          'manga_anime': 'anime, manga, anime style, manga style, masterpiece, full color, 2D anime manga illustration',
          'film': 'photorealistic, cinematic quality, professional lighting, film photography',
          'game_3d': '3D game character, stylized realism, game-ready, video game quality',
          'comic_cartoon': 'comic book style, western graphic novel, sharp ink lines, vibrant colors'
        };
        const archetypeBase = archetypePrompts[char.archetype] || '';

        // Get the specific substyle keywords (e.g., "dark_ova" specific tags)
        const style = getStyleById(char.archetype as CharacterArchetype, char.stylePreset);
        const substyleKeywords = style?.promptKeywords?.join(', ') || '';

        // Combine both: archetype base + substyle specifics
        if (archetypeBase && substyleKeywords) {
          return `${archetypeBase}, ${substyleKeywords}`;
        }
        return archetypeBase || substyleKeywords;
      };

      if (selectedCharacters2D.length === 1) {
        // Single character - use their usage_prompt directly
        const char = selectedCharacters2D[0];
        const styleKeywords = getStyleKeywords(char);

        // usagePrompt already contains character description and reference sheet instructions
        // We just need to add style keywords at the beginning, NOT the full description again
        if (char.usagePrompt) {
          if (char.usagePrompt.includes('[USER_PROMPT_PLACEHOLDER]')) {
            finalPrompt = char.usagePrompt.replace('[USER_PROMPT_PLACEHOLDER]', enhancedPrompt);
          } else {
            finalPrompt = `${char.usagePrompt} ${enhancedPrompt}`;
          }
        }

        // Add style keywords at the beginning if consistency is on
        // NOTE: usagePrompt already has the character description, so we DON'T add textDescription again
        if (styleConsistency && styleKeywords) {
          // Only prepend style keywords - usagePrompt handles the rest
          finalPrompt = `${styleKeywords}, ${finalPrompt}`;
          console.log('🎨 Applied style keywords:', styleKeywords);
        } else if (styleConsistency) {
          // No style keywords but consistency is on - just log it
          console.log('🎨 Style consistency on, but no style keywords available');
        }

        // Convert to CharacterRef format for imageGeneration
        characterRef = {
          id: char.id,
          name: char.name,
          snapshotUrl: char.referenceSheetUrl,
          metadata: {
            textDescription: char.textDescription,
            usagePrompt: char.usagePrompt,
            archetype: char.archetype,       // Base style (e.g., 'manga_anime')
            stylePreset: char.stylePreset    // Specific substyle (e.g., 'dark_ova')
          }
        };
        console.log('🎭 Using character reference:', char.name, 'with reference sheet, archetype:', char.archetype, 'style:', char.stylePreset);
      } else if (selectedCharacters2D.length === 2) {
        // Two characters - combine their descriptions with coherence instructions
        const char1 = selectedCharacters2D[0];
        const char2 = selectedCharacters2D[1];
        const styleKeywords = getStyleKeywords(char1); // Use first char's style (they should match if consistency is on)

        // Build a multi-character prompt with clear separation and coherence
        let multiCharPrompt = `MULTI-CHARACTER SCENE with exactly TWO distinct characters interacting together:

CHARACTER 1 - "${char1.name}": ${char1.textDescription}

CHARACTER 2 - "${char2.name}": ${char2.textDescription}

SCENE: ${enhancedPrompt}

CRITICAL CHARACTER CONSISTENCY REQUIREMENTS:
- Both characters must appear together in the same scene
- EXACT physical match required: hair color, eye color, skin tone, face shape, body type must precisely match descriptions above
- Each character must be clearly distinguishable - do NOT swap or blend features between them
- Maintain pixel-perfect accuracy to each character's unique traits (scars, tattoos, piercings, etc.)
- Consistent art style between both characters`;

        // Add style keywords if consistency is on
        if (styleKeywords) {
          multiCharPrompt = `${styleKeywords}, ${multiCharPrompt}`;
          console.log('🎨 Applied style keywords:', styleKeywords);
        }

        finalPrompt = multiCharPrompt;

        // Use first character's reference sheet as primary
        characterRef = {
          id: char1.id,
          name: `${char1.name} & ${char2.name}`,
          snapshotUrl: char1.referenceSheetUrl,
          metadata: {
            textDescription: `${char1.textDescription} | ${char2.textDescription}`,
            usagePrompt: multiCharPrompt,
            archetype: char1.archetype,       // Base style from first character
            stylePreset: char1.stylePreset    // Specific substyle from first character
          }
        };
        console.log('🎭 Using dual character references:', char1.name, '&', char2.name, 'archetype:', char1.archetype, 'style:', char1.stylePreset);
      }
    }

    try {
      // Pass both CharacterRef (for API) and Character2DRef (for state restoration)
      const character2DRef = selectedCharacters2D.length > 0 ? selectedCharacters2D[0] : undefined;
      await onGenerate(finalPrompt, modelId, {
        aspectRatio,
        inputImage,
        referenceImages: isChatGPTModelSelected
          ? [...images, ...selectedCharacters2D.map(character => character.referenceSheetUrl)]
          : undefined,
        selections: activeSelections,
        characterReference: characterRef,
        character2DRef,
        resolution: selections.resolution
      }, promptValue);
    } catch (e: any) {
      console.error("Generation error caught:", e);

      // Handle insufficient credits error
      if (e.code === 'INSUFFICIENT_CREDITS') {
        setCreditsRequired(e.required || currentCreditCost);
        setShowCreditsModal(true);
        return;
      }

      let msg = e.message || "An error occurred during generation.";

      // Parse JSON inside error message if present (e.g. from Replicate or backend)
      // Look for pattern {"error":...}
      const jsonMatch = msg.match(/\{"error":.*?\}/);
      if (jsonMatch) {
        try {
          const parsed = JSON.parse(jsonMatch[0]);
          if (parsed.error) msg = parsed.error;
        } catch (err) { /* ignore parse error */ }
      } else if (msg.includes("Generation failed:")) {
        msg = msg.replace("Generation failed:", "").trim();
      }

      // Specific handling for NSFWe / Sensitive content
      if (msg.includes("sensitive") || msg.includes("E005") || msg.includes("NSFW")) {
        msg = "⚠️ Generation Blocked: Content flagged as sensitive/NSFW. Please adjust your prompt.";
      }

      setErrorMessage(msg);
      setShowErrorModal(true);
    }
  }

  const shufflePrompt = () => {
    const random = examples[Math.floor(Math.random() * examples.length)];
    setPromptValue(random);
  }

  const addImage = (url: string) => {
    if (!url) return;
    setImages(prev => {
      const filtered = prev.filter(img => img !== url);
      // Check capacity only if adding a NEW image (filtered length == prev length)
      if (prev.length === filtered.length && filtered.length >= maxImages) {
        return prev;
      }
      return [...filtered, url];
    });
    setShowImageDropdown(false);
    setShowLinkInput(false);
    setLinkUrl("");
  };

  const handleDeviceUpload = () => {
    fileInputRef.current?.click();
  };

  // Helper derivations
  const supportsStandardFilters = !activeModel.includes('Animagine');

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!supportsImage) {
      showToast("This model does not support image inputs.", "error");
      return;
    }
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const remainingSlots = maxImages - images.length;
    if (remainingSlots <= 0) return;

    const maxFileSize = 25 * 1024 * 1024; // 25MB in bytes
    const filesToProcess = Array.from(files).slice(0, remainingSlots);
    const safeUserId = user?.id || 'anonymous';

    // Check file sizes before processing
    for (const file of filesToProcess) {
      if (file.size > maxFileSize) {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
        showToast(`Image "${file.name}" is too large (${sizeMB}MB). Maximum size is 25MB.`, 'error');
        return;
      }
    }

    // Upload to R2 uploads folder for session persistence
    const uploadFile = async (file: File): Promise<string> => {
      try {
        const blob = new Blob([file], { type: file.type });
        const filename = `input_${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '')}`;
        const r2Url = await uploadCharacterImageToR2(blob, safeUserId, filename, `lookbook/${safeUserId}/uploads`);
        console.log('📤 Uploaded input image to R2:', r2Url);
        return r2Url;
      } catch (error) {
        console.error('Failed to upload to R2, falling back to base64:', error);
        // Fallback to base64 if R2 upload fails
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target?.result as string);
          reader.readAsDataURL(file);
        });
      }
    };

    try {
      const uploadedUrls = await Promise.all(filesToProcess.map(uploadFile));
      setImages(prev => [...prev, ...uploadedUrls]);
      setShowImageDropdown(false);
    } catch (error) {
      console.error("Error uploading files", error);
    }

    if (event.target) event.target.value = '';
  };

  const containerClasses = variant === 'docked'
    ? "w-full bg-transparent flex flex-col"
    : "w-full mx-auto relative z-30 group";

  const innerClasses = variant === 'docked'
    ? "relative bg-transparent flex flex-col"
    : "relative bg-zinc-950/80 backdrop-blur-md border border-zinc-800 rounded-lg shadow-2xl flex flex-col";

  const inputClasses = variant === 'docked'
    ? "px-3 py-1.5 border-b border-zinc-800 bg-transparent rounded-t-lg"
    : "px-3 py-1.5 border-b border-zinc-800";

  const filterClasses = variant === 'docked'
    ? "flex flex-wrap items-stretch justify-between p-2 bg-transparent rounded-b-lg gap-2"
    : "flex flex-wrap items-center p-2 sm:p-2 bg-zinc-950 rounded-b-lg gap-2";

  // Position Logic for Dropdowns
  const isDocked = variant === 'docked';
  // If docked, menus open UP (bottom-full). If floating, menus open DOWN (top-full).
  const dropdownDirectionClass = isDocked ? 'bottom-full mb-2 origin-bottom' : 'top-full mt-1 origin-top';

  useEffect(() => {
    const hasPrompt = promptValue.trim().length > 0;
    const hasFilters = Object.entries(selections).some(([k, v]) => k !== 'ratio' && (Array.isArray(v) ? v.length > 0 : v && v !== 'None' && v !== 'Default'));
    const hasImages = images.length > 0;
    onStateChange?.({ hasPrompt, hasFilters, hasImages });
  }, [promptValue, selections, images, onStateChange]);

  return (
    <div className={containerClasses}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
        accept="image/*"
        multiple
      />

      {variant === 'floating' && (
        <div className="absolute -inset-[1px] bg-zinc-900/20 rounded-lg blur-sm" />
      )}

      <div className={innerClasses}>
        {/* Top Bar: Input */}
        {/* Top Bar: Input (Always mounted to persist text, hidden via CSS) */}
        <div className={`${inputClasses} relative min-h-[40px] transition-colors cursor-text ${isFocused ? 'bg-zinc-900/50' : ''} ${activeTool === 'prompt' ? '' : 'hidden'}`}>
          {/* Text Flow Container */}
          <div
            className="text-base font-medium leading-6 pr-8"
            style={{ fontFamily: '"Zen Maru Gothic", sans-serif' }}
          >
            {/* Filter Tags - rendered inline */}
            {Object.entries(selections)
              .filter(([key, val]) => {
                if (key === 'ratio' || key === 'resolution') return false; // Don't show ratio/resolution in prompt
                if (Array.isArray(val)) return val.length > 0;
                return val && val !== 'None' && val !== 'Default';
              })
              .sort(([keyA], [keyB]) => {
                const order = [
                  'animagine_char',
                  'animagine_attire',
                  'animagine_physique',
                  'animagine_creature',
                  'animagine_skin',
                  'animagine_hair',
                  'animagine_eyes',
                  'animagine_emotion',
                  'animagine_style',
                  'animagine_activity',
                  'animagine_shot',
                  'animagine_angle',
                  'animagine_focus',
                  'animagine_lighting',
                  'animagine_weather',
                  'animagine_time',
                  'animagine_location',
                  'era',
                  'rating'
                ];
                const idxA = order.indexOf(keyA);
                const idxB = order.indexOf(keyB);
                return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
              })
              .flatMap(([key, val]) => {
                const mapping = PROMPT_MAPPINGS[key];
                if (Array.isArray(val)) {
                  return val.map((v, i) => {
                    const tag = mapping ? mapping[v] : v;
                    if (!tag) return null;
                    const weight = filterWeights[key];
                    const displayTag = weight ? `${tag} (${weight}x)` : tag;
                    return (
                      <span
                        key={`${key}-${i}`}
                        onClick={() => cycleFilterWeight(key)}
                        className={`cursor-pointer hover:opacity-70 transition-opacity ${getWeightColor(weight)}`}
                        title={weight ? `Weight: ${weight}x (click to cycle)` : 'Click to add emphasis'}
                      >
                        {displayTag},{' '}
                      </span>
                    );
                  });
                }
                const tag = mapping ? mapping[val as string] : val;
                if (!tag) return null;
                const weight = filterWeights[key];
                const displayTag = weight ? `${tag} (${weight}x)` : tag;
                return (
                  <span
                    key={key}
                    onClick={() => cycleFilterWeight(key)}
                    className={`cursor-pointer hover:opacity-70 transition-opacity ${getWeightColor(weight)}`}
                    title={weight ? `Weight: ${weight}x (click to cycle)` : 'Click to add emphasis'}
                  >
                    {displayTag},{' '}
                  </span>
                );
              })}

            {/* User Input */}
            <span
              ref={promptInputRef}
              contentEditable
              role="textbox"
              className={`outline-none text-white empty:before:content-[attr(data-placeholder)] empty:before:text-zinc-500 ${isFocused ? 'caret-white' : 'caret-transparent'
                }`}
              style={{
                minWidth: '4px',
                minHeight: '1.5em',
                display: 'inline-block',
                verticalAlign: 'top',
                caretColor: isFocused ? 'white' : 'transparent',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}
              data-placeholder={
                isFocused
                  ? ""
                  : Object.entries(selections).some(([k, v]) => k !== 'ratio' && k !== 'resolution' && v && v !== 'None' && v !== 'Default')
                    ? ""
                    : typedPlaceholder
              }
              onInput={(e) => {
                const text = e.currentTarget.innerText;
                setPromptValue(text);
                onInputChange?.();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleGenerateClick();
                }
              }}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              suppressContentEditableWarning
            />
          </div>

          {/* Clear Button - Absolute positioned */}
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setPromptValue("")}
            className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md hover:bg-white/10 text-zinc-500 hover:text-white transition-all ${(promptValue && isFocused) ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>


        {/* Builder Tool Grid */}
        {activeTool === 'builder' && (
          <div className={`${inputClasses} flex flex-col items-stretch gap-6 p-6 overflow-x-auto min-h-[200px]`}>
            {/* Character Selector Row */}
            <CharacterSelector selectedId={selectedCharacter?.id || null} onSelect={setSelectedCharacter} />

            <div className="flex flex-col md:flex-row items-start gap-8">

              {/* Group: CHARACTER */}
              <div className="grid grid-cols-2 gap-2 min-w-[280px] flex-1">
                <span className="col-span-2 text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-2 border-b border-zinc-800 pb-1">Character</span>
                {supportedFilters.includes('animagine_char') && <DropdownMenu label="Subject" icon={UserIcon} active={activeDropdown === 'animagine_char'} optionsKey="animagine_char" toggleDropdown={toggleDropdown} selectOption={selectOption} selections={selections} options={options} variant={variant} align="left" />}
                {supportedFilters.includes('animagine_physique') && <DropdownMenu label="Physique" icon={UserIcon} active={activeDropdown === 'animagine_physique'} optionsKey="animagine_physique" toggleDropdown={toggleDropdown} selectOption={selectOption} selections={selections} options={options} variant={variant} align="left" multiSelect={true} />}
                {supportedFilters.includes('animagine_hair') && <DropdownMenu label="Hair" icon={UserIcon} active={activeDropdown === 'animagine_hair'} optionsKey="animagine_hair" toggleDropdown={toggleDropdown} selectOption={selectOption} selections={selections} options={options} variant={variant} align="left" multiSelect={true} />}
                {supportedFilters.includes('animagine_eyes') && <DropdownMenu label="Eyes" icon={EyeIcon} active={activeDropdown === 'animagine_eyes'} optionsKey="animagine_eyes" toggleDropdown={toggleDropdown} selectOption={selectOption} selections={selections} options={options} variant={variant} align="left" multiSelect={true} />}
                {supportedFilters.includes('animagine_skin') && <DropdownMenu label="Skin" icon={UserIcon} active={activeDropdown === 'animagine_skin'} optionsKey="animagine_skin" toggleDropdown={toggleDropdown} selectOption={selectOption} selections={selections} options={options} variant={variant} align="left" />}
                {supportedFilters.includes('animagine_attire') && <DropdownMenu label="Attire" icon={PaletteIcon} active={activeDropdown === 'animagine_attire'} optionsKey="animagine_attire" toggleDropdown={toggleDropdown} selectOption={selectOption} selections={selections} options={options} variant={variant} align="left" multiSelect={true} />}
                {supportedFilters.includes('animagine_creature') && <DropdownMenu label="Traits" icon={GhostIcon} active={activeDropdown === 'animagine_creature'} optionsKey="animagine_creature" toggleDropdown={toggleDropdown} selectOption={selectOption} selections={selections} options={options} variant={variant} align="left" multiSelect={true} />}
              </div>

              {/* Group: ACTION */}
              <div className="grid grid-cols-2 gap-2 min-w-[280px] flex-1">
                <span className="col-span-2 text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-2 border-b border-zinc-800 pb-1">Action</span>
                {supportedFilters.includes('animagine_emotion') && <DropdownMenu label="Emotion" icon={UserIcon} active={activeDropdown === 'animagine_emotion'} optionsKey="animagine_emotion" toggleDropdown={toggleDropdown} selectOption={selectOption} selections={selections} options={options} variant={variant} align="left" multiSelect={true} />}
                {supportedFilters.includes('animagine_activity') && <DropdownMenu label="Activity" icon={UserIcon} active={activeDropdown === 'animagine_activity'} optionsKey="animagine_activity" toggleDropdown={toggleDropdown} selectOption={selectOption} selections={selections} options={options} variant={variant} align="left" />}
              </div>

              {/* Group: ENVIRONMENT */}
              <div className="grid grid-cols-2 gap-2 min-w-[280px] flex-1">
                <span className="col-span-2 text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-2 border-b border-zinc-800 pb-1">Environment</span>
                {supportedFilters.includes('animagine_location') && <DropdownMenu label="Location" icon={MapPinIcon} active={activeDropdown === 'animagine_location'} optionsKey="animagine_location" toggleDropdown={toggleDropdown} selectOption={selectOption} selections={selections} options={options} variant={variant} align="left" />}
                {supportedFilters.includes('animagine_weather') && <DropdownMenu label="Weather" icon={CloudIcon} active={activeDropdown === 'animagine_weather'} optionsKey="animagine_weather" toggleDropdown={toggleDropdown} selectOption={selectOption} selections={selections} options={options} variant={variant} align="left" />}
                {supportedFilters.includes('animagine_lighting') && <DropdownMenu label="Light" icon={SunIcon} active={activeDropdown === 'animagine_lighting'} optionsKey="animagine_lighting" toggleDropdown={toggleDropdown} selectOption={selectOption} selections={selections} options={options} variant={variant} align="left" />}
                {supportedFilters.includes('animagine_time') && <DropdownMenu label="Time" icon={ClockIcon} active={activeDropdown === 'animagine_time'} optionsKey="animagine_time" toggleDropdown={toggleDropdown} selectOption={selectOption} selections={selections} options={options} variant={variant} align="left" />}
                {supportedFilters.includes('era') && <DropdownMenu label="Era" icon={ClockIcon} active={activeDropdown === 'era'} optionsKey="era" toggleDropdown={toggleDropdown} selectOption={selectOption} selections={selections} options={options} variant={variant} align="left" />}
              </div>

              {/* Group: DESIGN */}
              <div className="grid grid-cols-2 gap-2 min-w-[280px] flex-1">
                <span className="col-span-2 text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-2 border-b border-zinc-800 pb-1">Design</span>
                {supportedFilters.includes('animagine_style') && <DropdownMenu label="Style" icon={PaletteIcon} active={activeDropdown === 'animagine_style'} optionsKey="animagine_style" toggleDropdown={toggleDropdown} selectOption={selectOption} selections={selections} options={options} variant={variant} align="left" multiSelect={true} />}
                {supportedFilters.includes('animagine_shot') && <DropdownMenu label="Framing" icon={CropIcon} active={activeDropdown === 'animagine_shot'} optionsKey="animagine_shot" toggleDropdown={toggleDropdown} selectOption={selectOption} selections={selections} options={options} variant={variant} align="left" />}
                {supportedFilters.includes('animagine_angle') && <DropdownMenu label="Angle" icon={EyeIcon} active={activeDropdown === 'animagine_angle'} optionsKey="animagine_angle" toggleDropdown={toggleDropdown} selectOption={selectOption} selections={selections} options={options} variant={variant} align="left" />}
                {supportedFilters.includes('animagine_focus') && <DropdownMenu label="Focus" icon={ApertureIcon} active={activeDropdown === 'animagine_focus'} optionsKey="animagine_focus" toggleDropdown={toggleDropdown} selectOption={selectOption} selections={selections} options={options} variant={variant} align="left" />}
                {supportedFilters.includes('animagine_artist') && <DropdownMenu label="Artist" icon={PaletteIcon} active={activeDropdown === 'animagine_artist'} optionsKey="animagine_artist" toggleDropdown={toggleDropdown} selectOption={selectOption} selections={selections} options={options} variant={variant} align="left" />}
                {supportedFilters.includes('rating') && <DropdownMenu label="Rating" icon={LockIcon} active={activeDropdown === 'rating'} optionsKey="rating" toggleDropdown={toggleDropdown} selectOption={selectOption} selections={selections} options={options} variant={variant} align="left" />}
              </div>
            </div>
          </div>

        )}

        {/* Middle Bar: Image Tray */}
        {(activeTool === 'upload' || images.length > 0) && (
          <div className="relative px-4 py-3 bg-zinc-950 border-b border-zinc-800 flex gap-3 overflow-x-auto">
            {images.map((img, i) => (
              <div key={i} className="relative w-14 h-14 rounded-md overflow-hidden border border-zinc-700 group flex-shrink-0 cursor-pointer">
                <img src={img} alt="Reference" className="w-full h-full object-cover" />
                <button
                  onClick={() => setImages(images.filter((_, idx) => idx !== i))}
                  className="absolute top-1 right-1 p-1 rounded-full bg-zinc-900/50 text-white hover:bg-zinc-700 backdrop-blur-sm transition-colors opacity-0 group-hover:opacity-100"
                  title="Close preview"
                >
                  <XIcon className="w-3 h-3" />
                </button>
              </div>
            ))}
            {Array.from({ length: Math.max(0, maxImages - images.length) }).map((_, i) => (
              <button
                key={`empty-${i}`}
                disabled={!supportsImage}
                title={!supportsImage ? "Current model does not support images" : "Add Image"}
                onClick={() => {
                  if (supportsImage) setShowImageDropdown(true);
                }}
                className={`w-14 h-14 rounded-md border border-dashed border-zinc-800 flex flex-col items-center justify-center transition-all flex-shrink-0 group ${!supportsImage ? 'opacity-30 cursor-not-allowed bg-zinc-900/50' : 'text-zinc-600 hover:text-zinc-400 hover:bg-zinc-900'
                  }`}
              >
                <PlusIcon className={`w-4 h-4 transition-opacity ${!supportsImage ? 'opacity-20' : 'opacity-50 group-hover:opacity-100'}`} />
              </button>
            ))}

            {/* Upload Options Dropdown */}
            {showImageDropdown && (
              <div ref={imageDropdownRef} className="absolute left-4 top-1/2 -translate-y-1/2 z-50 bg-zinc-950 border border-zinc-800 rounded-lg shadow-xl animate-in fade-in zoom-in-95 overflow-hidden">
                {!showLinkInput ? (
                  <div className="p-1 min-w-[160px]">
                    <button
                      onClick={handleDeviceUpload}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-300 hover:bg-zinc-800 rounded transition-colors"
                    >
                      <UploadIcon className="w-4 h-4" />
                      Upload from Device
                    </button>
                    <button
                      onClick={() => setShowLinkInput(true)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-300 hover:bg-zinc-800 rounded transition-colors"
                    >
                      <LinkIcon className="w-4 h-4" />
                      Paste Image URL
                    </button>
                  </div>
                ) : (
                  <div className="p-3 min-w-[280px]">
                    <div className="flex items-center gap-2 mb-2">
                      <button
                        onClick={() => setShowLinkInput(false)}
                        className="p-1 text-zinc-500 hover:text-zinc-300 transition-colors"
                      >
                        <CaretLeftIcon className="w-4 h-4" />
                      </button>
                      <span className="text-xs text-zinc-400">Paste Image URL</span>
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={linkUrl}
                        onChange={(e) => setLinkUrl(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && linkUrl.trim()) {
                            addImage(linkUrl.trim());
                          }
                        }}
                        placeholder="https://example.com/image.jpg"
                        className="flex-1 px-3 py-1.5 text-xs bg-zinc-900 border border-zinc-800 rounded-md text-zinc-300 placeholder:text-zinc-600 focus:outline-none focus:border-zinc-700"
                        autoFocus
                      />
                      <button
                        onClick={() => linkUrl.trim() && addImage(linkUrl.trim())}
                        disabled={!linkUrl.trim()}
                        className="px-3 py-1.5 text-xs font-medium bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Character Avatar Selection Bar - shown when toggled for Nano Banana */}
        {supportsCharacterReferences && showCharacterDropdown && (
          <div ref={characterBarRef} className="border-b border-zinc-800 bg-zinc-900/30">
            {/* Header row - only show when there are controls to display */}
            {(selectedCharacters2D.length > 0 || styleConflictNotification) && (
              <div className="flex items-center gap-2 px-3 pt-2 pb-1">
                {/* Style consistency toggle - only show when character is selected */}
                {selectedCharacters2D.length > 0 && (
                  <button
                    onClick={() => setStyleConsistency(prev => !prev)}
                    className={`flex-shrink-0 p-1.5 rounded transition-all border ${styleConsistency
                      ? 'border-emerald-500/50 bg-emerald-500/20 text-emerald-400'
                      : 'border-zinc-700 bg-zinc-800 text-zinc-500 hover:border-zinc-600'
                      }`}
                    title={styleConsistency ? 'Style consistency ON - characters must have matching styles' : 'Style consistency OFF - any characters can be combined'}
                  >
                    <PaletteIcon className="w-4 h-4" weight={styleConsistency ? 'fill' : 'regular'} />
                  </button>
                )}

                {/* Selection count indicator */}
                {selectedCharacters2D.length > 0 && (
                  <span className="text-[10px] text-zinc-500">
                    {selectedCharacters2D.length === 1 ? '1 selected' : '2 selected'}
                  </span>
                )}

                {/* Style conflict notification */}
                {styleConflictNotification && (
                  <div className="flex-shrink-0 px-2 py-1 rounded bg-amber-500/20 border border-amber-500/30 text-amber-400 text-[10px] whitespace-nowrap animate-pulse">
                    {styleConflictNotification}
                  </div>
                )}
              </div>
            )}

            {/* Character avatars - wrapping grid */}
            <div className={`flex flex-wrap items-center gap-3 px-3 ${selectedCharacters2D.length > 0 || styleConflictNotification ? 'pb-3' : 'py-3'}`}>
              {isLoadingCharacters ? (
                <div className="text-xs text-zinc-500 px-2">Loading...</div>
              ) : projectCharacters.length === 0 ? (
                <div className="text-xs text-zinc-500 px-2 whitespace-nowrap">
                  {selectedProjectId ? 'No characters in project' : 'No finalized characters'}
                </div>
              ) : (
                projectCharacters.map((char) => {
                  const isSelected = selectedCharacters2D.some(c => c.id === char.id);
                  const selectionIndex = selectedCharacters2D.findIndex(c => c.id === char.id);
                  // Check for style conflict with currently selected character
                  const hasStyleConflict = styleConsistency &&
                    selectedCharacters2D.length === 1 &&
                    selectedCharacters2D[0].stylePreset !== char.stylePreset;
                  const isBlocked = !isSelected && (selectedCharacters2D.length >= 2 || hasStyleConflict);
                  return (
                    <button
                      key={char.id}
                      onClick={() => {
                        setStyleConflictNotification(null);
                        if (isSelected) {
                          // Remove from selection and update prompt
                          const newSelection = selectedCharacters2D.filter(c => c.id !== char.id);
                          setSelectedCharacters2D(newSelection);
                          // Update prompt with remaining character name or clear
                          if (newSelection.length === 1) {
                            setPromptValue(prev => prev.replace(new RegExp(`\\b${char.name}\\b\\s*(and|&)?\\s*`, 'i'), '').trim());
                          } else if (newSelection.length === 0) {
                            setPromptValue(prev => prev.replace(new RegExp(`\\b${char.name}\\b`, 'i'), '').trim());
                          }
                        } else if (hasStyleConflict) {
                          // Show style conflict notification
                          setStyleConflictNotification(`Style conflict: ${selectedCharacters2D[0].name} uses "${selectedCharacters2D[0].stylePreset}" but ${char.name} uses "${char.stylePreset}"`);
                          setTimeout(() => setStyleConflictNotification(null), 3000);
                        } else if (selectedCharacters2D.length < 2) {
                          // Add to selection and auto-insert name into prompt
                          const newSelection = [...selectedCharacters2D, char];
                          setSelectedCharacters2D(newSelection);
                          // Auto-insert character names into prompt (only if not already present)
                          if (newSelection.length === 1) {
                            setPromptValue(prev => {
                              // Check if name is already at the start of the prompt (case-insensitive)
                              const namePattern = new RegExp(`^${char.name}\\b`, 'i');
                              if (namePattern.test(prev)) {
                                return prev; // Name already present, don't add again
                              }
                              return prev ? `${char.name} ${prev}` : char.name;
                            });
                          } else if (newSelection.length === 2) {
                            // Replace first character's name with "Name1 and Name2"
                            const firstName = newSelection[0].name;
                            setPromptValue(prev => prev.replace(new RegExp(`^${firstName}\\s*`, 'i'), `${firstName} and ${char.name} `));
                          }
                        }
                      }}
                      className={`relative flex-shrink-0 w-24 h-24 rounded-full overflow-hidden transition-all border-2 ${isSelected
                        ? 'border-indigo-500 shadow-[0_0_15px_rgba(99,102,241,0.5)] scale-105'
                        : isBlocked
                          ? 'border-zinc-800 opacity-40 cursor-not-allowed'
                          : 'border-zinc-700 hover:border-zinc-500 hover:scale-[1.03]'
                        }`}
                      title={isSelected ? `${char.name} (click to deselect)` : hasStyleConflict ? 'Conflicting style' : selectedCharacters2D.length >= 2 ? 'Max 2 characters' : char.name}
                      disabled={isBlocked}
                    >
                      <img
                        src={char.headImageUrl}
                        alt={char.name}
                        className="w-full h-full object-cover"
                        style={{ objectPosition: `center ${char.avatarYPosition}%` }}
                      />
                      {isSelected && (
                        <div className="absolute bottom-1 right-1 w-7 h-7 bg-indigo-500 rounded-full flex items-center justify-center text-xs font-bold text-white shadow-lg">
                          {selectionIndex + 1}
                        </div>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Bottom Bar: Model Selection & Filters */}
        <div className={filterClasses}>

          {/* LEFT: Model & Model-Based Filters (Ratio) */}
          <div className="flex flex-wrap items-center gap-2 pl-1">
            {/* Model Selector */}
            <div className="relative" ref={modelDropdownRef}>
              <button
                ref={modelButtonRef}
                onClick={() => setShowModelDropdown(!showModelDropdown)}
                className={`
                  flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-all border whitespace-nowrap
                  ${showModelDropdown
                    ? 'bg-zinc-900 border-zinc-700 text-white'
                    : 'bg-zinc-900 border-zinc-800 text-zinc-300 hover:bg-zinc-800'}
                `}
              >
                <span className="w-3.5 h-3.5 flex-shrink-0">{getModelIcon(activeModel)}</span>
                <span className="truncate text-left">{activeModel}</span>
                <CaretDownIcon className={`w-3 h-3 text-zinc-500 transition-transform ${showModelDropdown ? 'rotate-180' : ''}`} />
              </button>

              {showModelDropdown && createPortal(
                <div
                  ref={modelDropdownMenuRef}
                  style={{
                    position: 'fixed',
                    zIndex: 9999,
                    top: modelDropdownCoords.top,
                    bottom: modelDropdownCoords.bottom,
                    left: modelDropdownCoords.left,
                  }}
                  className="w-56 bg-zinc-950 border border-zinc-800 rounded-lg overflow-hidden shadow-2xl animate-in fade-in zoom-in-95"
                >
                  <div className="max-h-60 overflow-y-auto p-1">
                    {models.map(m => {
                      // In Lookbook mode, gate models by minimumTier (e.g., Nano Banana Pro requires starter+)
                      const userTier = (user?.subscription_tier || 'free') as 'free' | 'starter' | 'pro';
                      const isLocked = viewMode === 'lookbook' && !canAccessModel(userTier, m.modelId);
                      const modelCost = getCreditCost(m.modelId);
                      return (
                        <button
                          key={m.name}
                          onClick={() => {
                            if (!isLocked) {
                              setActiveModel(m.name);
                              // Reset ratio and resolution defaults based on model
                              // Nano Banana (Flash) only supports 1K, Nano Banana Pro supports 1K/2K/4K
                              if (m.name.includes('Animagine')) {
                                setSelections(prev => ({ ...prev, ratio: 'Photo (3:2)', resolution: 'High Quality' }));
                              } else if (m.name === 'Nano Banana Pro') {
                                setSelections(prev => ({ ...prev, ratio: 'Square (1:1)', resolution: '2K' }));
                              } else if (m.name === 'Nano Banana') {
                                setSelections(prev => ({ ...prev, ratio: 'Square (1:1)', resolution: '1080p' })); // Flash only supports 1K
                              } else {
                                setSelections(prev => ({ ...prev, ratio: 'Square (1:1)', resolution: m.modelId.startsWith('openai/') ? 'Medium' : 'High Quality' }));
                              }
                              setShowModelDropdown(false);
                            } else {
                              showToast?.('Upgrade to Starter or Pro to use this model in Lookbook', 'info');
                            }
                          }}
                          className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between transition-colors rounded
                                            ${activeModel === m.name ? 'text-white bg-zinc-800' : 'text-zinc-400 hover:bg-zinc-800/50'}
                                            ${isLocked ? 'opacity-50 cursor-not-allowed' : ''}
                                        `}
                        >
                          <div className="flex items-center gap-3">
                            {getModelIcon(m.name)}
                            {m.name}
                          </div>
                          <div className="flex items-center gap-2">
                            {!isLocked && <CreditCostBadge cost={modelCost} balance={creditBalance} />}
                            {isLocked && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-500 font-medium border border-zinc-700">
                                UPGRADE
                              </span>
                            )}
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </div>,
                document.body
              )}
            </div>

            {/* Upload support indicator */}
            {models.find(m => m.name === activeModel)?.supportsImage && (
              <div className="flex items-center gap-1 px-2 py-1.5 rounded bg-zinc-800/50 border border-zinc-800 text-[10px] text-zinc-400" title="This model supports image inputs">
                <ImageIcon className="w-3 h-3" />
                <span className="hidden sm:inline">Img2Img</span>
              </div>
            )}

            {/* Error Modal */}
            {showErrorModal && (
              <div className="fixed inset-0 z-[200] flex items-center justify-center bg-zinc-900/60 backdrop-blur-sm">
                <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-6 w-96 shadow-2xl animate-in fade-in zoom-in-95">
                  <div className="mb-4">
                    <h3 className="text-sm font-bold text-red-400 mb-2 flex items-center gap-2">
                      Error
                    </h3>
                    <p className="text-xs text-zinc-300 leading-relaxed bg-zinc-900/50 p-3 rounded-lg border border-white/5 font-mono break-words">
                      {errorMessage}
                    </p>
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={() => setShowErrorModal(false)}
                      className="px-4 py-2 text-xs font-medium text-white bg-zinc-800 border border-zinc-700/50 rounded-lg hover:bg-zinc-700 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Model-Based Filter: Ratio */}
            <DropdownMenu
              label="Ratio" icon={CropIcon} active={activeDropdown === 'ratio'}
              optionsKey="ratio" toggleDropdown={toggleDropdown} selectOption={selectOption} selections={selections} options={options}
              variant={variant} align="left"
            />

            {/* Resolution Quality - hidden for Animagine (uses fixed dimensions per aspect ratio) */}
            {!activeModel.includes('Animagine') && !activeModel.startsWith('Qwen Image 2.1') && (
              <DropdownMenu
                label="Quality" icon={ArrowsOutIcon} active={activeDropdown === 'resolution'}
                optionsKey="resolution" toggleDropdown={toggleDropdown} selectOption={selectOption} selections={selections} options={options}
                variant={variant} align="left"
              />
            )}

            {/* Character Selector Button - only for Nano Banana models */}
            {supportsCharacterReferences && (
              <button
                ref={characterDropdownRef as React.RefObject<HTMLButtonElement>}
                onClick={() => setShowCharacterDropdown(!showCharacterDropdown)}
                className={`relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap
                  ${selectedCharacters2D.length > 0
                    ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30'
                    : showCharacterDropdown
                      ? 'bg-white/10 text-zinc-300 border border-white/10'
                      : 'bg-white/5 text-zinc-400 border border-white/5 hover:bg-white/10 hover:text-zinc-300'
                  }`}
              >
                <UserIcon className="w-3.5 h-3.5" />
                <span>Character</span>
                {/* Badge showing count when characters selected and bar is closed */}
                {selectedCharacters2D.length > 0 && !showCharacterDropdown && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-indigo-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow-lg">
                    {selectedCharacters2D.length}
                  </span>
                )}
              </button>
            )}
          </div>



          {/* RIGHT: Actions */}
          <div className="flex flex-wrap items-center gap-2">

            {/* Tabs */}
            <div className="flex items-center gap-1">
              {/* Tools - BUILDER only shows for Animagine model */}
              {['PROMPT', ...(activeModel.includes('Animagine') ? ['BUILDER'] : []), 'UPLOAD'].map(mode => {
                const isActive = activeTool === mode.toLowerCase();
                const hasContent = mode === 'PROMPT' ? (promptValue?.trim().length > 0)
                  : mode === 'BUILDER' ? Object.entries(selections).some(([k, v]) => k !== 'ratio' && (Array.isArray(v) ? v.length > 0 : v && v !== 'None' && v !== 'Default'))
                    : mode === 'UPLOAD' ? (images.length > 0)
                      : false;

                const isDisabled = mode === 'UPLOAD' && !supportsImage;

                return (
                  <button
                    key={mode}
                    onClick={() => !isDisabled && onToolChange?.(mode.toLowerCase())}
                    disabled={isDisabled}
                    className={`px-3 py-1.5 text-xs font-medium transition-all rounded-md whitespace-nowrap
                          ${isActive
                        ? 'text-white bg-zinc-900 border border-zinc-800 shadow-sm'
                        : isDisabled
                          ? 'text-zinc-600 cursor-not-allowed decoration-zinc-800'
                          : hasContent
                            ? 'text-zinc-300 hover:bg-zinc-800/50 border border-transparent'
                            : 'text-zinc-500 border border-transparent hover:text-zinc-300 hover:bg-zinc-800/50'}`}
                    title={isDisabled ? "Current model does not support image input" : ""}
                  >
                    {mode}
                  </button>
                );
              })}
            </div>

            {/* Generate Button (Always Visible) */}
            <div className="flex items-center gap-1.5 shrink-0">
              {user && <CreditCostBadge cost={currentCreditCost} balance={creditBalance} />}
              <button
                onClick={handleGenerateClick}
                disabled={isGenerating}
                className={`group h-8 w-8 rounded-lg flex items-center justify-center transition-all shrink-0
                        ${isGenerating
                    ? 'bg-indigo-600/50 cursor-not-allowed'
                    : 'bg-indigo-600 hover:bg-indigo-500 active:scale-95'}
                        text-white shadow-lg shadow-indigo-500/20`}
                title={isGenerating ? 'Generating...' : (user ? `Generate (${formatCreditCost(currentCreditCost)})` : 'Try Free')}
              >
                {isGenerating ? (
                  <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                ) : (
                  <ArrowRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Generator Tabs */}

      </div>

      {/* Insufficient Credits Modal */}
      <InsufficientCreditsModal
        isOpen={showCreditsModal}
        onClose={() => setShowCreditsModal(false)}
        required={creditsRequired}
        balance={creditBalance}
      />
    </div >
  );
};

// Dummy Icon helper
const BoxIcon = (props: any) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" /></svg>
)

const DropdownMenu = ({ label, icon: Icon, active, optionsKey, toggleDropdown, selectOption, selections, options, variant, align = 'left', multiSelect = false }: any) => {
  const selectedValues = multiSelect ? (selections[optionsKey] || []) : selections[optionsKey];
  const isSelected = multiSelect ? selectedValues.length > 0 : (selectedValues && selectedValues !== 'None');
  const isClearable = isSelected && optionsKey !== 'ratio' && optionsKey !== 'resolution';

  const buttonRef = useRef<HTMLButtonElement>(null);
  const [coords, setCoords] = useState<{ top?: number, bottom?: number, left?: number, right?: number }>({});

  useEffect(() => {
    if (active && buttonRef.current) {
      const updatePosition = () => {
        const rect = buttonRef.current?.getBoundingClientRect();
        if (!rect) return;
        const spaceBelow = window.innerHeight - rect.bottom;
        const openUp = spaceBelow < 300;

        const newCoords: any = {};
        if (openUp) {
          newCoords.bottom = window.innerHeight - rect.top + 4;
        } else {
          newCoords.top = rect.bottom + 4;
        }

        if (align === 'right') {
          newCoords.right = window.innerWidth - rect.right;
        } else {
          newCoords.left = rect.left;
        }
        setCoords(newCoords);
      };
      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true); // true for capture to catch all scrolls
      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition, true);
      };
    }
  }, [active, align]);

  // Menu Content State
  const [visibleCount, setVisibleCount] = useState(50);
  useEffect(() => {
    if (!active) {
      setVisibleCount(50);
    }
  }, [active]);

  const allOptions = options[optionsKey] || [];
  const visibleOptions = allOptions.slice(0, visibleCount);
  const hasMore = visibleCount < allOptions.length;

  const handleSelect = (optLabel: string) => {
    if (multiSelect) {
      const current = selections[optionsKey] || [];
      if (current.includes(optLabel)) {
        selectOption(optionsKey, current.filter((v: string) => v !== optLabel));
      } else {
        selectOption(optionsKey, [...current, optLabel]);
      }
    } else {
      selectOption(optionsKey, optLabel);
    }
  };

  const isOptSelected = (optLabel: string) => {
    if (multiSelect) {
      return (selections[optionsKey] || []).includes(optLabel);
    }
    return selections[optionsKey] === optLabel;
  };

  const displayLabel = multiSelect
    ? (selectedValues.length > 0 ? `${label} (${selectedValues.length})` : label)
    : (isSelected ? selections[optionsKey] : label);

  const dropdownContent = active && createPortal(
    <div
      style={{ ...coords, position: 'fixed', zIndex: 9999 }}
      className="min-w-[12rem] w-max max-w-[300px] bg-zinc-950 border border-zinc-800 rounded-lg overflow-hidden shadow-2xl animate-in fade-in zoom-in-95"
    >
      <div className="p-1 max-h-64 overflow-y-auto custom-scrollbar">
        {visibleOptions.map((opt: any) => (
          <button
            key={opt.label}
            onClick={() => handleSelect(opt.label)}
            className={`w-full text-left px-3 py-2 text-xs hover:bg-zinc-800 rounded flex items-center gap-2 transition-colors ${isOptSelected(opt.label) ? 'text-white bg-zinc-800' : 'text-zinc-400'}`}
          >
            {multiSelect && (
              <div className={`w-3 h-3 border rounded-sm flex items-center justify-center ${isOptSelected(opt.label) ? 'bg-purple-500 border-purple-500' : 'border-zinc-600'}`}>
                {isOptSelected(opt.label) && <span className="text-[8px] text-white">✓</span>}
              </div>
            )}
            <opt.icon className={`w-3 h-3 flex-shrink-0 ${isOptSelected(opt.label) ? 'text-purple-400' : 'text-zinc-600'}`} />
            <span className="whitespace-nowrap">{opt.label}</span>
          </button>
        ))}
        {hasMore && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setVisibleCount((prev: number) => prev + 50);
            }}
            className="w-full text-center px-3 py-2 text-xs text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 rounded transition-colors border-t border-zinc-800 mt-1"
          >
            See More
          </button>
        )}
      </div>
    </div>,
    document.body
  );

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => toggleDropdown(optionsKey)}
        className={`
            flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-all border whitespace-nowrap
            ${active || isSelected
            ? 'bg-zinc-900 border-zinc-700 text-white'
            : 'bg-zinc-900 border-zinc-800 text-zinc-300 hover:bg-zinc-800'}
          `}
      >
        <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${isSelected ? 'text-white' : 'text-zinc-400'}`} />
        <span className="truncate text-left">{displayLabel}</span>

        {isClearable ? (
          <div
            role="button"
            onClick={(e) => {
              e.stopPropagation();
              selectOption(optionsKey, multiSelect ? [] : 'None');
            }}
            className="ml-1 hover:text-white transition-colors"
          >
            <XIcon className="w-3 h-3" />
          </div>
        ) : (
          <CaretDownIcon className={`w-3 h-3 text-zinc-500 transition-transform ${active ? 'rotate-180' : ''}`} />
        )}
      </button>

      {dropdownContent}
    </>
  );
};
